// ICIC_NOTIFICATIONS_V1
// ICIC_POSTER_APP_INTEGRATION_V1
import {
  Capacitor,
  CapacitorHttp
} from '@capacitor/core';

import {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  getEffectiveNow,
  getEffectiveToday,
  getSystemTimeState,
  setSystemTime
} from '../../../utils/systemTime';
import {
  expandStudentAssignmentScheduleOccurrences,
  loadStudentAssignmentScheduleContext
} from '../../../utils/studentAssignmentSchedule';
import {
  applyStudentAssignmentTiming,
  getStudentAssignmentDate
} from '../../../utils/assignmentTiming';
import { fetchStudentChats } from '../chat/ChatContext';
import Chat from '../chat/Chat';
import VotingDisplay from '../VotingDisplay';
import StudentSchedule from '../course/StudentSchedule';
import StudentProgress from '../course/StudentProgress';
import StudentGlobalGroups, {
  studentHasAnyCurrentGroups
} from '../StudentGlobalGroups';
import StudentGlobalSchedule from '../StudentGlobalSchedule';
import {
  effectiveClassroomForWeek
} from '../../../utils/classroomResolution';
import StudentPd from '../course/StudentPd';
import StudentCq from '../course/StudentCq';
import StudentQi from '../course/StudentQi';
import StudentSelfEval from '../course/StudentSelfEval';
import StudentSyllabus from '../course/StudentSyllabus';
import StudentAssignments from '../course/StudentAssignments';
import StudentDocuments from '../course/StudentDocuments';
import StudentReadings from '../course/StudentReadings';
import StudentInstructor from '../course/StudentInstructor';
import { Preferences } from '@capacitor/preferences';
import StudentAllCourseReadings from '../StudentAllCourseReadings';
import { getNativeDeviceToken } from '../../../utils/nativeDeviceAuth';
import {
  ensureStudentPushRegistration
} from '../../../utils/studentPushNotifications';
import MobileNotificationSettings from './MobileNotificationSettings';
import StudentProfile from '../StudentProfile';
import {
  listStudentDevices,
  revokeStudentDevice
} from '../devices/deviceSessions';
import './MobileStudentApp.css';
import {
  scanForVeritasSignal
} from '../../../native/veritasBle';
import MobileEvents from './MobileEvents';
import ICICPortal from '../../../components/ICICPortal';
import ICICPosterSubmission from '../../../components/ICICPosterSubmission';

import {
  loadIcicConferenceState,
  loadIcicPosterState
} from '../../../utils/icic';

import {
  listStudentHonorsEvents,
  honorsEventCategory,
  honorsEventRegistrationLabel
} from '../../../utils/honorsEvents';

import {
  listStudentHonorsPolls
} from '../../../utils/honorsPolls';

import HonorsPollForm
  from '../HonorsPollForm';

import {
  createStudentHonorsFormUrl,
  getStudentHonorsPortalState,
  isCurrentHonorsProgramMember
} from '../../../utils/honorsSignups';
import {
  requestStudentDataRefresh,
  resetStudentDataRefreshGate,
  setStudentDataRefreshSafe,
  useStudentDataRefreshTick
} from '../../../utils/studentDataRefresh';
import CoursePlacement
  from '../../../components/CoursePlacement';

import {
  fetchVisibleStudentNotifications
} from '../../../utils/studentNotifications';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;

const courseImageCache = new Map();

function apiHeaders() {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY
  };
}

async function readJson(response, label) {
  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  return text ? JSON.parse(text) : null;
}


async function submitStudentAttendance({
  studentId,
  courseId = null,
  testMode = false,
  effectiveAt = null,
  signalToken = null,
  deviceToken = null
}) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/student_attendance_check_in`,
    {
      method: 'POST',
      headers: {
        ...apiHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        p_student_id: studentId,
        p_course_id: courseId,
        p_test_mode: testMode,
        p_effective_at: effectiveAt,
        p_signal_token: signalToken,
        p_device_token: deviceToken
      })
    }
  );

  return readJson(response, 'Recording attendance');
}

function getCourseName(enrolment) {
  return (
    enrolment?.courses?.course_titles?.name ||
    enrolment?.courses?.name ||
    'Untitled Course'
  );
}

function getStudentName(session) {
  return (
    session?.englishName ||
    session?.chineseName ||
    session?.studentCode ||
    'Student'
  );
}

function firstName(session) {
  const name = String(getStudentName(session)).trim();
  return name.split(/\s+/)[0] || name;
}

function formatShortDate(dateStr) {
  if (!dateStr) return '';

  return new Date(`${String(dateStr).slice(0, 10)}T12:00:00`).toLocaleDateString(
    'en-GB',
    {
      day: 'numeric',
      month: 'short'
    }
  );
}

function formatLongDate(date) {
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
}

function toDateTimeLocalInput(date) {
  return [
    [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-'),
    [
      String(date.getHours()).padStart(2, '0'),
      String(date.getMinutes()).padStart(2, '0')
    ].join(':')
  ].join('T');
}

function formatDeviceMoment(value) {
  if (!value) return 'Unknown';

  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function devicePlatformLabel(value) {
  if (value === 'ios') return 'iOS';
  if (value === 'android') return 'Android';
  return value ? String(value) : 'Mobile';
}

function dayName(dateStr) {
  if (!dateStr) return '';

  return new Date(`${String(dateStr).slice(0, 10)}T12:00:00`).toLocaleDateString(
    'en-GB',
    { weekday: 'long' }
  );
}

function shortDay(dateStr) {
  if (!dateStr) return '';

  return new Date(`${String(dateStr).slice(0, 10)}T12:00:00`).toLocaleDateString(
    'en-GB',
    { weekday: 'short' }
  );
}

function courseTimeForDate(course, dateStr) {
  const day = dayName(dateStr);

  if (course?.class_times?.[day]) {
    return String(course.class_times[day]).slice(0, 5);
  }

  if (course?.class_time) {
    return String(course.class_time).slice(0, 5);
  }

  return '';
}

function getStudentAttendanceSchedule(
  attendanceSchedules,
  enrolmentId
) {
  return (
    attendanceSchedules?.[
      String(enrolmentId || '')
    ] || {
      enabledWeekIds: [],
      timeOverrides: {},
      roomOverrides: {}
    }
  );
}

function studentHasClassOnWeek(
  enrolment,
  week,
  attendanceSchedules
) {
  const course =
    enrolment?.courses;

  if (!course || !week) {
    return false;
  }

  if (
    course.group_based_attendance ===
    true
  ) {
    const schedule =
      getStudentAttendanceSchedule(
        attendanceSchedules,
        enrolment.id
      );

    return new Set(
      (
        schedule.enabledWeekIds ||
        []
      ).map(String)
    ).has(
      String(week.id || '')
    );
  }

  return (
    week.attendance_enabled === true
  );
}

function effectiveClassTimeForStudent(
  enrolment,
  week,
  attendanceSchedules
) {
  const course =
    enrolment?.courses;

  const normal =
    courseTimeForDate(
      course,
      week?.class_date
    );

  if (
    course?.variable_timings_enabled !==
    true
  ) {
    return normal;
  }

  if (
    course?.group_based_attendance ===
    true
  ) {
    const schedule =
      getStudentAttendanceSchedule(
        attendanceSchedules,
        enrolment.id
      );

    return String(
      schedule.timeOverrides?.[
        String(week?.id || '')
      ] ||
      normal ||
      ''
    ).slice(0, 5);
  }

  return String(
    week?.start_time_override ||
    normal ||
    ''
  ).slice(0, 5);
}

function effectiveClassRoomForStudent(
  enrolment,
  week,
  attendanceSchedules
) {
  const course =
    enrolment?.courses;

  const schedule =
    course
      ?.group_based_attendance ===
      true
      ? getStudentAttendanceSchedule(
          attendanceSchedules,
          enrolment?.id
        )
      : null;

  return effectiveClassroomForWeek(
    course,
    week,
    schedule
      ?.roomOverrides
      ?.[String(week?.id || '')] ||
      null
  );
}


function courseSchedule(course) {
  const days =
    Array.isArray(course?.class_days) && course.class_days.length
      ? course.class_days.filter(Boolean)
      : course?.class_day
        ? [course.class_day]
        : [];

  if (!days.length) return '';

  return days
    .map(day => {
      const time =
        course?.class_times?.[day] ||
        (
          days.length === 1
            ? course?.class_time
            : null
        );

      const room =
        course?.classrooms?.[day] ||
        '';

      return [
        day.slice(0, 3),

        time
          ? String(time)
              .slice(0, 5)
          : null,

        room || null
      ]
        .filter(Boolean)
        .join(' ');
    })
    .join(' · ');
}

function semesterStillCurrent(startDate, today) {
  if (!startDate) return false;

  const start = new Date(`${startDate}T12:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 18 * 7);

  return new Date(`${today}T12:00:00`) <= end;
}

async function fetchCurrentEnrolments(studentId, today) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?student_id=eq.${studentId}&active=eq.true&select=*,courses(*,course_titles(name),semesters(name,start_date))`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(response, 'Loading current courses');

  return (Array.isArray(rows) ? rows : []).filter(enrolment => {
    if (!enrolment?.courses?.is_live) return false;

    return semesterStillCurrent(
      enrolment?.courses?.semesters?.start_date,
      today
    );
  });
}

async function fetchPreviousEnrolments(studentId, today) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?student_id=eq.${studentId}&select=*,courses(*,course_titles(name),semesters(name,start_date))`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(response, 'Loading previous courses');

  return (Array.isArray(rows) ? rows : []).filter(enrolment => {
    const startDate = enrolment?.courses?.semesters?.start_date;
    if (!startDate) return false;

    const start = new Date(`${startDate}T12:00:00`);
    const end = new Date(start);
    end.setDate(end.getDate() + 18 * 7);

    return new Date(`${today}T12:00:00`) > end;
  });
}

async function fetchUpcomingWeeks(courseIds, today) {
  if (!courseIds.length) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=in.(${courseIds.join(',')})&class_date=gte.${today}&select=id,course_id,week_number,class_date,cq_enabled,pd_enabled,attendance_enabled,start_time_override,classroom_override&order=class_date.asc&limit=100`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(response, 'Loading course schedule');

  return Array.isArray(rows) ? rows : [];
}

async function fetchStudentAttendanceSchedules(
  enrolmentIds
) {
  const ids = (
    Array.isArray(enrolmentIds)
      ? enrolmentIds
      : []
  )
    .filter(Boolean)
    .map(String);

  const result =
    Object.fromEntries(
      ids.map(id => [
        id,
        {
          enabledWeekIds: [],
          timeOverrides: {},
          roomOverrides: {}
        }
      ])
    );

  if (!ids.length) {
    return result;
  }

  const membershipResponse =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/attendance_group_memberships?enrolment_id=in.(' +
        ids.join(',') +
        ')&select=enrolment_id,attendance_group_id',
      {
        headers: apiHeaders()
      }
    );

  const memberships =
    await readJson(
      membershipResponse,
      'Loading attendance group membership'
    );

  const groupIds = [
    ...new Set(
      (Array.isArray(memberships)
        ? memberships
        : []
      )
        .map(
          row =>
            row.attendance_group_id
        )
        .filter(Boolean)
        .map(String)
    )
  ];

  if (!groupIds.length) {
    return result;
  }

  const scheduleResponse =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/attendance_group_weeks?attendance_group_id=in.(' +
        groupIds.join(',') +
        ')&select=attendance_group_id,course_week_id,start_time_override,classroom_override',
      {
        headers: apiHeaders()
      }
    );

  const rows =
    await readJson(
      scheduleResponse,
      'Loading attendance group schedule'
    );

  const rowsByGroup =
    new Map();

  (Array.isArray(rows)
    ? rows
    : []
  ).forEach(row => {
    const groupId =
      String(
        row.attendance_group_id ||
        ''
      );

    if (!groupId) {
      return;
    }

    if (
      !rowsByGroup.has(groupId)
    ) {
      rowsByGroup.set(
        groupId,
        []
      );
    }

    rowsByGroup
      .get(groupId)
      .push(row);
  });

  (Array.isArray(memberships)
    ? memberships
    : []
  ).forEach(membership => {
    const enrolmentId =
      String(
        membership.enrolment_id ||
        ''
      );

    const groupId =
      String(
        membership.attendance_group_id ||
        ''
      );

    if (
      !enrolmentId ||
      !groupId
    ) {
      return;
    }

    if (!result[enrolmentId]) {
      result[enrolmentId] = {
        enabledWeekIds: [],
        timeOverrides: {},
        roomOverrides: {}
      };
    }

    const schedule =
      result[enrolmentId];

    const seenWeeks =
      new Set(
        schedule.enabledWeekIds
          .map(String)
      );

    (
      rowsByGroup.get(groupId) ||
      []
    ).forEach(row => {
      if (!row.course_week_id) {
        return;
      }

      const weekId =
        String(
          row.course_week_id
        );

      if (!seenWeeks.has(weekId)) {
        seenWeeks.add(weekId);

        schedule.enabledWeekIds.push(
          weekId
        );
      }

      if (row.start_time_override) {
        schedule.timeOverrides[
          weekId
        ] = String(
          row.start_time_override
        ).slice(0, 5);
      }

      if (
        String(
          row.classroom_override ||
          ''
        ).trim()
      ) {
        schedule.roomOverrides[
          weekId
        ] = String(
          row.classroom_override
        ).trim();
      }
    });
  });

  return result;
}

async function fetchAssignments(courseIds) {
  if (!courseIds.length) return [];

  const response = await fetch(
   `${SUPABASE_URL}/rest/v1/course_assignments?course_id=in.(${courseIds.join(',')})&select=id,course_id,assignment_type_id,groups_enabled,group_count,open_date,due_date,occurs_on,assignment_types(name,requires_submission,is_core)&order=due_date.asc`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(response, 'Loading assignments');

  return Array.isArray(rows) ? rows : [];
}

async function fetchAssignmentTypeSettings(courseIds) {
  if (!courseIds.length) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=in.(${courseIds.join(',')})&select=course_id,assignment_type_id,display_name,menu_order,group_deadlines_enabled,sequence_id,schedule_enabled,schedule_choice_enabled,schedule_open_offset_days,schedule_due_offset_days,schedule_choice_open_date,schedule_choice_close_date,schedule_choice_required_count`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading assignment menu order'
  );

  return Array.isArray(rows) ? rows : [];
}


async function fetchAssignmentSequences(courseIds) {
  if (!courseIds.length) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_sequences?course_id=in.(${courseIds.join(',')})&select=id,course_id,name&order=created_at.asc`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading assignment sequences'
  );

  return Array.isArray(rows) ? rows : [];
}


async function fetchStudentAssignmentGroupMemberships(
  enrolmentIds
) {
  if (!enrolmentIds.length) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_memberships?enrolment_id=in.(${enrolmentIds.join(',')})&select=course_assignment_id,enrolment_id,group_number`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading assignment group membership'
  );

  return Array.isArray(rows) ? rows : [];
}


async function fetchStudentAssignmentGroupDeadlines(
  courseIds
) {
  if (!courseIds.length) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_group_deadlines?select=course_assignment_id,group_number,open_date,due_date,course_assignments!inner(course_id)&course_assignments.course_id=in.(${courseIds.join(',')})`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading assignment group deadlines'
  );

  return Array.isArray(rows) ? rows : [];
}


async function fetchStudentAssignmentSubmissionTiming(
  enrolmentIds
) {
  if (!enrolmentIds.length) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions?enrolment_id=in.(${enrolmentIds.join(',')})&select=course_assignment_id,enrolment_id,course_assignment_schedule_item_id,structured_form_instance_id,submitted,drive_file_id,file_name,text,extension_days`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading assignment extensions'
  );

  return Array.isArray(rows) ? rows : [];
}


async function fetchCourseOnboarding(studentId, courseIds) {
  if (!courseIds.length) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/student_course_onboarding?student_id=eq.${studentId}&course_id=in.(${courseIds.join(',')})&select=*`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(response, 'Loading course onboarding');

  return Array.isArray(rows) ? rows : [];
}

async function createCourseOnboarding(studentId, courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/student_course_onboarding`,
    {
      method: 'POST',
      headers: {
        ...apiHeaders(),
        'Content-Type': 'application/json',
        Prefer: 'resolution=merge-duplicates,return=minimal'
      },
      body: JSON.stringify({
        student_id: studentId,
        course_id: courseId
      })
    }
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      `Creating course onboarding failed (${response.status}): ${text || response.statusText}`
    );
  }
}

async function loadCourseOnboarding(studentId, courseIds) {
  const rows = await fetchCourseOnboarding(studentId, courseIds);
  const existingIds = new Set(rows.map(row => String(row.course_id)));
  const missingIds = courseIds.filter(id => !existingIds.has(String(id)));

  if (missingIds.length) {
    await Promise.all(
      missingIds.map(courseId => createCourseOnboarding(studentId, courseId))
    );
  }

  return [
    ...rows,
    ...missingIds.map(courseId => ({
      student_id: studentId,
      course_id: courseId,
      syllabus_completed: false,
      contract_completed: false
    }))
  ];
}

async function fetchCourseHasSyllabus(courseId) {
  const [
    sectionsResponse,
    descriptionsResponse
  ] = await Promise.all([
    fetch(
      SUPABASE_URL +
        '/rest/v1/course_syllabus_sections' +
        '?course_id=eq.' +
        encodeURIComponent(
          courseId
        ) +
        '&select=course_id&limit=1',
      {
        headers:
          apiHeaders()
      }
    ),

    fetch(
      SUPABASE_URL +
        '/rest/v1/course_assignment_type_descriptions' +
        '?course_id=eq.' +
        encodeURIComponent(
          courseId
        ) +
        '&select=course_id&limit=1',
      {
        headers:
          apiHeaders()
      }
    )
  ]);

  const [
    sections,
    descriptions
  ] = await Promise.all([
    readJson(
      sectionsResponse,
      'Loading syllabus sections'
    ),

    readJson(
      descriptionsResponse,
      'Loading syllabus assignment descriptions'
    )
  ]);

  return (
    (
      Array.isArray(
        sections
      ) &&
      sections.length > 0
    ) ||
    (
      Array.isArray(
        descriptions
      ) &&
      descriptions.length > 0
    )
  );
}


function courseById(enrolments, courseId) {
  return enrolments.find(
    enrolment => String(enrolment?.courses?.id) === String(courseId)
  );
}

function nextWeekForCourse(
  weeks,
  enrolment,
  attendanceSchedules
) {
  return (
    weeks.find(
      week =>
        String(week.course_id) ===
          String(
            enrolment?.courses?.id ||
            ''
          ) &&
        studentHasClassOnWeek(
          enrolment,
          week,
          attendanceSchedules
        )
    ) || null
  );
}

function nextEnabledWeekForCourse(
  weeks,
  courseId,
  field
) {
  return (
    weeks.find(
      week =>
        String(week.course_id) === String(courseId) &&
        week?.[field] === true
    ) || null
  );
}

function nextAssignmentsForCourse(
  assignments,
  courseId,
  today
) {
  const upcoming = assignments
    .filter(
      assignment =>
        String(assignment.course_id) ===
          String(courseId)
    )
    .filter(assignment => {
      const date =
        getStudentAssignmentDate(
          assignment
        );

      return (
        date &&
        String(date).slice(0, 10) >=
          today
      );
    })
    .sort((a, b) => {
      const aDate = String(
        getStudentAssignmentDate(a) || ''
      ).slice(0, 10);

      const bDate = String(
        getStudentAssignmentDate(b) || ''
      ).slice(0, 10);

      return aDate.localeCompare(bDate);
    });

  const nextBySequenceOrType =
    new Map();

    upcoming.forEach(assignment => {
    const sequenceId =
      assignment.effective_sequence_id;

    const key = sequenceId
      ? `sequence:${sequenceId}`
      : `type:${
          assignment.assignment_type_id ||
          assignment.assignment_types?.name ||
          assignment.id
        }`;

    const mapKey = String(key);
    const existing =
      nextBySequenceOrType.get(mapKey);

    if (!existing) {
      nextBySequenceOrType.set(
        mapKey,
        assignment
      );
      return;
    }

    const existingDate = String(
      getStudentAssignmentDate(existing) || ''
    ).slice(0, 10);

    const candidateDate = String(
      getStudentAssignmentDate(assignment) || ''
    ).slice(0, 10);

    if (
      candidateDate &&
      (!existingDate || candidateDate < existingDate)
    ) {
      nextBySequenceOrType.set(
        mapKey,
        assignment
      );
    }
  });

  return Array.from(
    nextBySequenceOrType.values()
  ).sort((a, b) => {
    const aDate = String(
      getStudentAssignmentDate(a) || ''
    ).slice(0, 10);

    const bDate = String(
      getStudentAssignmentDate(b) || ''
    ).slice(0, 10);

    return aDate.localeCompare(bDate);
  });
}

function nextUpItem(
  enrolments,
  weeks,
  assignments,
  today,
  attendanceSchedules
) {
  const candidates = [];

  weeks.forEach(week => {
    const enrolment =
      courseById(
        enrolments,
        week.course_id
      );

    if (!enrolment) {
      return;
    }

    if (
      !studentHasClassOnWeek(
        enrolment,
        week,
        attendanceSchedules
      )
    ) {
      return;
    }

    const effectiveTime =
      effectiveClassTimeForStudent(
        enrolment,
        week,
        attendanceSchedules
      );

    candidates.push({
      kind: 'class',
      date: week.class_date,
      label: 'Next class',
      title: getCourseName(enrolment),
      meta: [
        (
          shortDay(
            week.class_date
          ) +
          (
            effectiveTime
              ? ' ' + effectiveTime
              : ''
          )
        ).trim(),

        effectiveClassRoomForStudent(
          enrolment,
          week,
          attendanceSchedules
        ) || null
      ]
        .filter(Boolean)
        .join(' · ')
    });
  });

  const assignmentCourseIds = [
    ...new Set(
      assignments
        .map(
          assignment =>
            assignment.course_id
        )
        .filter(Boolean)
    )
  ];

  assignmentCourseIds
    .flatMap(courseId =>
      nextAssignmentsForCourse(
        assignments,
        courseId,
        today
      )
    )
    .forEach(assignment => {
      const enrolment =
        courseById(
          enrolments,
          assignment.course_id
        );

      if (!enrolment) return;

      const requiresSubmission =
        assignment.assignment_types
          ?.requires_submission ?? true;

      const date =
        getStudentAssignmentDate(
          assignment
        );

      if (!date) return;

      candidates.push({
        kind: 'assignment',
        date: String(date).slice(0, 10),
         label:
          (assignment.assignment_types?.name || 'Assignment') +
          (
            assignment.schedule_source_title
              ? ` — ${assignment.schedule_source_title}`
              : ''
          ),
        title: getCourseName(enrolment),
        meta: requiresSubmission
          ? `Due ${formatShortDate(date)}`
          : formatShortDate(date)
      });
    });

  candidates.sort((a, b) => {
  const dateCompare = String(a.date).localeCompare(String(b.date));
  if (dateCompare !== 0) return dateCompare;
  // Tiebreak by time
  return String(a.meta || '').localeCompare(String(b.meta || ''));
});

  return candidates[0] || null;
}

function greeting(now) {
  const hour = now.getHours();

  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

async function loadNativeCourseImage(course) {
  const courseId = course?.id;
  const fileId = course?.course_image_drive_file_id;

  if (!courseId || !fileId || !API_URL) {
    return null;
  }

  if (courseImageCache.has(fileId)) {
    return courseImageCache.get(fileId);
  }

  // Check persistent device storage first
  try {
    const stored = await Preferences.get({ key: `course_img_${fileId}` });
    if (stored?.value) {
      courseImageCache.set(fileId, stored.value);
      return stored.value;
    }
  } catch (err) {
    console.error('Course image cache read failed:', err);
  }

  // Not cached — fetch from API
  const response = await CapacitorHttp.post({
    url: API_URL,
    headers: { 'Content-Type': 'application/json' },
    data: { action: 'getCourseImageData', courseId }
  });

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Course image request failed (${response.status}).`);
  }

  const data =
    typeof response.data === 'string'
      ? JSON.parse(response.data)
      : response.data;

  if (!data?.ok || !data.hasImage || !data.base64Data) {
    return null;
  }

  const src = `data:${data.mimeType || 'image/jpeg'};base64,${data.base64Data}`;

  courseImageCache.set(fileId, src);

  // Save to device storage for next app launch
  try {
    await Preferences.set({ key: `course_img_${fileId}`, value: src });
  } catch (err) {
    console.error('Course image cache write failed:', err);
  }

  return src;
}

function CourseArtwork({ course, compact = false }) {
  const name =
    course?.course_titles?.name ||
    course?.name ||
    'Course';

  const fileId = course?.course_image_drive_file_id;

  const [imageSrc, setImageSrc] = useState(
    () => courseImageCache.get(fileId) || null
  );

  useEffect(() => {
    let cancelled = false;

    if (!fileId) {
      setImageSrc(null);
      return () => {
        cancelled = true;
      };
    }

    const cached = courseImageCache.get(fileId);

    if (cached) {
      setImageSrc(cached);

      return () => {
        cancelled = true;
      };
    }

    setImageSrc(null);

    loadNativeCourseImage(course)
      .then(src => {
        if (!cancelled) {
          setImageSrc(src);
        }
      })
      .catch(error => {
        console.error(
          'Course image load failed:',
          error
        );

        if (!cancelled) {
          setImageSrc(null);
        }
      });

    return () => {
      cancelled = true;
    };
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.id, fileId]);

  if (imageSrc) {
    return (
      <img
        className={
          compact
            ? 'vm-course-image compact'
            : 'vm-course-image'
        }
        src={imageSrc}
        alt=""
      />
    );
  }

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();

  return (
    <div
      className={
        compact
          ? 'vm-course-fallback compact'
          : 'vm-course-fallback'
      }
    >
      {initials || 'C'}
    </div>
  );
}

function Icon({ name }) {
  const common = {
    width: 21,
    height: 21,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true
  };

  const paths = {
    home: (
      <>
        <path d="M3 10.8 12 3l9 7.8" />
        <path d="M5.5 9.5V21h13V9.5" />
      </>
    ),
    courses: (
      <>
        <path d="M4 5h16v14H4z" />
        <path d="M8 9h8M8 13h8" />
      </>
    ),
    checkin: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12 2.2 2.2 4.8-5" />
      </>
    ),
    groups: (
      <>
        <circle cx="8" cy="9" r="3" />
        <circle cx="16" cy="9" r="3" />
        <path d="M3 20a5 5 0 0 1 10 0" />
        <path d="M11 20a5 5 0 0 1 10 0" />
      </>
    ),
    chat: (
      <>
        <path d="M4 5.5h16v11H9l-5 4v-15Z" />
        <path d="M8 10h8M8 13h5" />
      </>
    ),
    events: (
      <>
        <rect x="4" y="5" width="16" height="15" rx="2" />
        <path d="M8 3v4M16 3v4M4 10h16" />
      </>
    ),
    more: (
      <>
        <circle cx="5" cy="12" r="1" />
        <circle cx="12" cy="12" r="1" />
        <circle cx="19" cy="12" r="1" />
      </>
    ),
    back: <path d="m15 18-6-6 6-6" />,
    arrow: <path d="m9 18 6-6-6-6" />
  };

  return <svg {...common}>{paths[name] || paths.more}</svg>;
}

function BottomNav({
  active,
  onChange,
  showEvents,
  showGroups,
  showCheckIn
}) {
  const items = [
    {
      key: 'home',
      label: 'Home',
      icon: 'home'
    },
    {
      key: 'courses',
      label: 'Courses',
      icon: 'courses'
    },
    ...(showCheckIn
      ? [{
          key: 'checkin',
          label: 'Check In',
          icon: 'checkin'
        }]
      : []),
    ...(showGroups
      ? [{
          key:
            'groups',

          label:
            'Groups',

          icon:
            'groups'
        }]
      : []),

    {
      key: 'chat',
      label: 'Chat',
      icon: 'chat'
    },

    ...(showEvents
      ? [{
          key: 'events',
          label: 'Events',
          icon: 'events'
        }]
      : []),

    {
      key: 'more',
      label: 'More',
      icon: 'more'
    }
  ];


  return (
    <nav
      className="vm-bottom-nav"
      aria-label="App navigation"
      style={{
        gridTemplateColumns:
          `repeat(${items.length}, minmax(0, 1fr))`
      }}
    >
      {items.map(
        item => (
          <button
            key={item.key}
            type="button"
            className={
              active ===
              item.key
                ? 'active'
                : ''
            }
            onClick={() =>
              onChange(
                item.key
              )
            }
          >
            <Icon
              name={
                item.icon
              }
            />

            <span>
              {item.label}
            </span>
          </button>
        )
      )}
    </nav>
  );
}

function ScreenHeader({ title, onBack, right = null }) {
  return (
    <header className="vm-topbar">
      <div className="vm-topbar-side">
        {onBack && (
          <button
            className="vm-icon-button"
            type="button"
            onClick={onBack}
            aria-label="Back"
          >
            <Icon name="back" />
          </button>
        )}
      </div>

      <h1>{title}</h1>

      <div className="vm-topbar-side right">{right}</div>
    </header>
  );
}

function CourseListRow({ enrolment, onOpen }) {
  const course = enrolment?.courses;

  return (
    <button
      className="vm-course-row"
      type="button"
      onClick={() => onOpen(course?.id)}
    >
      <CourseArtwork course={course} compact />

      <span className="vm-course-row-copy">
        <strong>{getCourseName(enrolment)}</strong>
        <small>
          {courseSchedule(course)}
          {course?.semesters?.name
            ? `${courseSchedule(course) ? ' · ' : ''}${course.semesters.name}`
            : ''}
        </small>
      </span>

      <span className="vm-row-arrow">
        <Icon name="arrow" />
      </span>
    </button>
  );
}

function HomeModeTabs({ active, onChange }) {
  return <div className="vm-home-mode-tabs">
    <button type="button" className={active === 'home' ? 'active' : ''} onClick={() => onChange('home')}>Home</button>
    <button type="button" className={active === 'schedule' ? 'active' : ''} onClick={() => onChange('schedule')}>Schedule</button>
  </div>;
}

function MobileScheduleHome({
  session,
  enrolments,
  honorsPortalState,
  onOpenHome
}) {
  return <div className="vm-scroll">
    <HomeModeTabs active="schedule" onChange={v => v === 'home' && onOpenHome()} />
    <StudentGlobalSchedule
      session={session}
      enrolments={enrolments}
      honorsPortalState={
        honorsPortalState
      }
      compact
    />
  </div>;
}

function MobileHome({
  session,
  enrolments,
  weeks,
  attendanceSchedules,
  assignments,
  notifications,
  honorsPortalState,
  honorsEvents,
  honorsPolls,
  icicPosterState,
  onOpenCourse,
  onOpenHonors,
  onOpenEvent,
  onOpenPoll,
  onOpenIcicPoster,
  onOpenSchedule
}) {
  const now = getEffectiveNow(session);
  const today = getEffectiveToday(session);
  const nextUp =
    nextUpItem(
      enrolments,
      weeks,
      assignments,
      today,
      attendanceSchedules
    );


  const program =
    honorsPortalState
      ?.program ||
    {};


  const registration =
    honorsPortalState
      ?.courseRegistration ||
    {};


  const selections =
    Array.isArray(
      registration.selections
    )
      ? registration.selections
      : [];


  const recommendations =
    Array.isArray(
      registration.recommendations
    )
      ? registration.recommendations
      : [];


  const statusCounts =
    selections.reduce(
      (counts, row) => {
        const label =
          row.displayStatus ||
          'Pending';

        counts[label] =
          (
            counts[label] ||
            0
          ) + 1;

        return counts;
      },
      {}
    );


  const statusSummary =
    [
      'Accepted',
      'Waitlisted',
      'Pending',
      'Not accepted'
    ]
      .filter(
        label =>
          statusCounts[label]
      )
      .map(
        label =>
          `${statusCounts[label]} ${label.toLowerCase()}`
      )
      .join(' · ');


  const showHonors =
    Boolean(
      registration.show
    );


  const registrationEvents =
    (
      Array.isArray(honorsEvents)
        ? honorsEvents
        : []
    ).filter(
      event =>
        event?.registration?.status !==
        'Registered'
    );


  return (
    <div className="vm-scroll">
      <HomeModeTabs active="home" onChange={v => v === 'schedule' && onOpenSchedule()} />
      <div className="vm-home-heading">
        <h1>{greeting(now)}, {firstName(session)}</h1>
        <p>{formatLongDate(now)}</p>
      </div>

      <section className="vm-section">
        <div className="vm-section-label">Next up</div>

        {nextUp ? (
          <div className="vm-priority-card">
            <span>{nextUp.label}</span>
            <strong>{nextUp.title}</strong>
            <small>{nextUp.meta}</small>
          </div>
        ) : (
          <div className="vm-empty">Nothing upcoming.</div>
        )}
      </section>

      {icicPosterState?.available &&
        (
          icicPosterState.submitOpen ||
          (
            Array.isArray(icicPosterState.posters) &&
            icicPosterState.posters.length > 0
          )
        ) && (
        <section className="vm-section">
          <div className="vm-section-label">
            ICIC
          </div>

          <div className="vm-honors-home-list">
            <button
              type="button"
              className="vm-honors-home-card"
              onClick={onOpenIcicPoster}
            >
              <span>Poster Submission</span>

              <strong>
                {Array.isArray(icicPosterState.posters) &&
                icicPosterState.posters.length > 0
                  ? 'ICIC Poster Submission'
                  : 'Submit a Poster to ICIC'}
              </strong>

              <small>
                {Array.isArray(icicPosterState.posters) &&
                icicPosterState.posters.length > 0
                  ? (
                      icicPosterState.posters.length +
                      ' project' +
                      (
                        icicPosterState.posters.length === 1
                          ? ''
                          : 's'
                      ) +
                      ' · Status, consent and final poster'
                    )
                  : (
                      icicPosterState?.submission?.closesOn
                        ? (
                            'Proposals close ' +
                            icicPosterState.submission.closesOn
                          )
                        : 'Poster proposals are open'
                    )}
              </small>

              <div className="vm-honors-home-arrow">
                <Icon name="arrow" />
              </div>
            </button>
          </div>
        </section>
      )}

      {honorsPolls.length > 0 && (
        <section className="vm-section">
          <div className="vm-section-label">
            Tunghai Honors Program
          </div>

          <div className="vm-honors-home-list">
            {honorsPolls.map(
              poll => (
                <button
                  type="button"
                  className="vm-honors-home-card"
                  key={poll.pollId}
                  onClick={() =>
                    onOpenPoll(
                      poll.pollId
                    )
                  }
                >
                  <span>
                    Honors Poll
                  </span>

                  <strong>
                    {poll.title}
                  </strong>

                  <small>
                    Closes {
                      formatShortDate(
                        poll.closeDate
                      )
                    }
                  </small>

                  <div className="vm-honors-home-arrow">
                    <Icon name="arrow" />
                  </div>
                </button>
              )
            )}
          </div>
        </section>
      )}

      {registrationEvents.length > 0 && (
        <section className="vm-section">
          <div className="vm-section-label">
            Tunghai Honors Program
          </div>

          <div className="vm-honors-home-list">
            {registrationEvents.map(
              event => (
                <button
                  type="button"
                  className="vm-honors-home-card"
                  style={{
                    borderLeft: '3px solid ' +
                      honorsEventCategory(event).accent
                  }}
                  key={event.eventId}
                  onClick={() =>
                    onOpenEvent(
                      event.eventId
                    )
                  }
                >
                  <span style={{
                    color: honorsEventCategory(event).text
                  }}>
                    {honorsEventRegistrationLabel(event)}
                  </span>

                  <strong>
                    {event.title}
                  </strong>

                  <small>
                    {[
                      formatShortDate(
                        event.eventDate
                      ),
                      event.startTime,
                      event.location
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>

                  <div className="vm-honors-home-arrow">
                    <Icon name="arrow" />
                  </div>
                </button>
              )
            )}
          </div>
        </section>
      )}

      {showHonors && (
        <section className="vm-section">
          <div className="vm-section-label">
            Tunghai Honors Program
          </div>

          <div className="vm-honors-home-list">
            {registration.show && (
              <button
                type="button"
                className="vm-honors-home-card"
                onClick={() =>
                  onOpenHonors(
                    'course_registration'
                  )
                }
              >
                <span>
                  Course Registration
                </span>

                <strong>
                  {registration.open
                    ? 'Course signup'
                    : 'Registration status'}
                </strong>

                <small>
                  {statusSummary ||
                    (
                      registration.open
                        ? 'Course signup is open.'
                        : 'View your current registration status.'
                    )}

                  {recommendations.length
                    ? ` · Recommended for ${recommendations.length} course${recommendations.length === 1 ? '' : 's'}`
                    : ''}
                </small>

                <div className="vm-honors-home-arrow">
                  <Icon name="arrow" />
                </div>
              </button>
            )}
          </div>
        </section>
      )}


      <section className="vm-section">
        <div className="vm-section-label">Current courses</div>

        <div className="vm-course-list">
          {enrolments.slice(0, 3).map(enrolment => (
            <CourseListRow
              key={enrolment.id}
              enrolment={enrolment}
              onOpen={onOpenCourse}
            />
          ))}
        </div>
      </section>

      <section className="vm-section">
        <div className="vm-section-label">Latest</div>

        {notifications.length ? (
          <div className="vm-latest-card">
            <strong>
              {notifications[0]?.courses?.course_titles?.name || 'Update'}
            </strong>
            <p>{notifications[0]?.message}</p>
          </div>
        ) : (
          <div className="vm-empty">No recent updates.</div>
        )}
      </section>

      {program.showApplication && (
        <section className="vm-section">
          <div className="vm-section-label">
            Tunghai Honors Program
          </div>

          <div className="vm-honors-home-list">
            <button
              type="button"
              className="vm-honors-home-card"
              onClick={() =>
                onOpenHonors(
                  'application'
                )
              }
            >
              <span>
                Application
              </span>

              <strong>
                Apply to the Tunghai Honors Program
              </strong>

              <small>
                {program
                  .nextInterviewConsiderationDeadline
                  ? `Applications received by ${formatShortDate(
                      program
                        .nextInterviewConsiderationDeadline
                    )} will be considered for the next interview round.`
                  : 'Applications are open.'}
              </small>

              <div className="vm-honors-home-arrow">
                <Icon name="arrow" />
              </div>
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function MobileCourses({ current, previous, onOpenCourse }) {
  return (
    <>
      <ScreenHeader title="Courses" />

      <div className="vm-scroll with-topbar">
        <section className="vm-section">
          <div className="vm-section-label">Current</div>

          <div className="vm-course-list">
            {current.map(enrolment => (
              <CourseListRow
                key={enrolment.id}
                enrolment={enrolment}
                onOpen={onOpenCourse}
              />
            ))}
          </div>
        </section>

        <section className="vm-section">
          <div className="vm-section-label">Previous</div>

          {previous.length ? (
            <div className="vm-previous-list">
              {previous.slice(0, 8).map(enrolment => (
                <div className="vm-previous-row" key={enrolment.id}>
                  <span>
                    <strong>{getCourseName(enrolment)}</strong>
                    <small>{enrolment?.courses?.semesters?.name || ''}</small>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="vm-empty">No previous courses.</div>
          )}
        </section>
      </div>
    </>
  );
}

function toolItems(
  course,
  courseAssignments = [],
  assignmentTypeSettings = [],
  assignmentSequences = []
) {
  const assignmentItems = [];
  const seenAssignmentTypes = new Set();
  const seenSequenceIds = new Set();

  const menuOrderByType = new Map();
  const sequenceByType = new Map();
  const sequenceById = new Map(
    assignmentSequences
      .filter(
        sequence =>
          String(sequence.course_id) ===
          String(course?.id)
      )
      .map(sequence => [
        String(sequence.id),
        sequence
      ])
  );

  assignmentTypeSettings
    .filter(
      row =>
        String(row.course_id) ===
        String(course?.id)
    )
    .forEach(row => {
      sequenceByType.set(
        String(row.assignment_type_id),
        row.sequence_id || null
      );

      const raw = row.menu_order;

      if (
        raw === null ||
        raw === undefined ||
        raw === ''
      ) {
        return;
      }

      const numeric = Number(raw);

      if (Number.isFinite(numeric)) {
        menuOrderByType.set(
          String(row.assignment_type_id),
          numeric
        );
      }
    });

  courseAssignments
    .filter(
      assignment =>
        assignment.assignment_types?.is_core !== true
    )
    .forEach((assignment, originalIndex) => {
      const typeId = assignment.assignment_type_id;
      if (!typeId || seenAssignmentTypes.has(String(typeId))) return;

      seenAssignmentTypes.add(String(typeId));

      const sequenceId =
        sequenceByType.get(String(typeId)) || null;

      if (sequenceId) {
        if (seenSequenceIds.has(String(sequenceId))) {
          return;
        }

        seenSequenceIds.add(String(sequenceId));

        const sequence =
          sequenceById.get(String(sequenceId));

        assignmentItems.push({
          key: `sequence:${sequenceId}`,
          toolKey: 'assignments',
          label: sequence?.name || 'Assignment Sequence',
          sequenceId,
          show: true,
          originalIndex,
          menuOrder:
            menuOrderByType.get(String(typeId))
        });

        return;
      }

      assignmentItems.push({
        key: `assignments:${typeId}`,
        toolKey: 'assignments',
        label: assignment.assignment_types?.name || 'Assignment',
        assignmentTypeId: typeId,
        show: true,
        originalIndex,
        menuOrder:
          menuOrderByType.get(String(typeId))
      });
    });

  assignmentItems.sort((a, b) => {
    const aHasOrder = Number.isFinite(a.menuOrder);
    const bHasOrder = Number.isFinite(b.menuOrder);

    if (aHasOrder && bHasOrder) {
      return (
        a.menuOrder - b.menuOrder ||
        a.originalIndex - b.originalIndex
      );
    }

    if (aHasOrder) return -1;
    if (bHasOrder) return 1;

    return a.originalIndex - b.originalIndex;
  });

  return [
    { key: 'schedule', label: 'Teaching Schedule', show: true },
    { key: 'readings', label: 'Readings', show: true },
    {
      key: 'progress',
      label: 'My Progress',
      show:
        course
          ?.course_progress_enabled !==
        false
    },
    {
      key: 'pd',
      label: 'Philosophical Diary',
      show: course?.pd_enabled !== false
    },
    {
      key: 'cq',
      label: 'Questions Assignment',
      show: course?.cq_enabled !== false
    },
    {
      key: 'qi',
      label: 'Question Improvement',
      show: course?.qi_enabled === true
    },
    ...assignmentItems,
    {
      key: 'selfeval',
      label: 'Self-Evaluation',
      show: course?.self_eval_enabled !== false
    },
    { key: 'syllabus', label: 'Syllabus', show: true },
{ key: 'documents', label: 'Documents', show: true },
{ key: 'instructor', label: 'Instructor Information', show: true },
{
  key: 'chat',
  label: 'Course Chat',
  show:
    course?.chat_enabled !==
    false
},
{
  key: 'voting',
  label: 'Class Voting',
  show: course?.cq_enabled !== false
}
  ].filter(item => item.show);
}

function getMobilePdDueDate(course, week) {
  if (!course?.pd_due_day || !week?.class_date) {
    return null;
  }

  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const targetDay = dayNames.indexOf(course.pd_due_day);

  if (targetDay === -1) {
    return null;
  }

  const parts = String(week.class_date)
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(Number.isFinite)
  ) {
    return null;
  }

  const dueDate = new Date(
    parts[0],
    parts[1] - 1,
    parts[2],
    12,
    0,
    0
  );

  while (dueDate.getDay() !== targetDay) {
    dueDate.setDate(dueDate.getDate() + 1);
  }

  return [
    dueDate.getFullYear(),
    String(dueDate.getMonth() + 1).padStart(2, '0'),
    String(dueDate.getDate()).padStart(2, '0')
  ].join('-');
}


function getMobileCqDeadline(course, week) {
  if (!week?.class_date) {
    return null;
  }

  const dateParts = String(week.class_date)
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (
    dateParts.length !== 3 ||
    !dateParts.every(Number.isFinite)
  ) {
    return null;
  }

  const classDayDate = new Date(
    dateParts[0],
    dateParts[1] - 1,
    dateParts[2]
  );

  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const classDay = dayNames[classDayDate.getDay()];

  const resolvedTime =
    course?.class_times?.[classDay] ||
    course?.class_time ||
    '09:00';

  const timeParts = String(resolvedTime)
    .slice(0, 5)
    .split(':')
    .map(Number);

  const hour =
    Number.isFinite(timeParts[0])
      ? timeParts[0]
      : 9;

  const minute =
    Number.isFinite(timeParts[1])
      ? timeParts[1]
      : 0;

  const classStart = new Date(
    dateParts[0],
    dateParts[1] - 1,
    dateParts[2],
    hour,
    minute,
    0,
    0
  );

  const configuredHours = Number(
    course?.cq_due_hours_before_class ?? 1
  );

  const hoursBefore =
    Number.isFinite(configuredHours) &&
    configuredHours >= 0
      ? configuredHours
      : 1;

  return {
    deadline: new Date(
      classStart.getTime() -
        hoursBefore * 60 * 60 * 1000
    ),
    hoursBefore
  };
}


function getMobileAssignmentOpenDate(assignment) {
  if (
    assignment?.student_assignment_applicable === false
  ) {
    return null;
  }

  if (
    assignment?.student_assignment_timing_resolved
  ) {
    return assignment.effective_open_date || null;
  }

  return assignment?.open_date || null;
}

function MobileGroups({
  session,
  enrolments
}) {
  return (
    <>
      <ScreenHeader
        title="Groups"
      />

      <div className="vm-scroll with-topbar">
        <StudentGlobalGroups
          session={
            session
          }
          enrolments={
            enrolments
          }
          compact
        />
      </div>
    </>
  );
}


function getMobileSelfEvalReminders(course, today) {
  if (!course || course.self_eval_enabled === false) return [];

  function closeWithExtension(dateString, days) {
    const dateText = String(dateString || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateText)) return null;

    const date = new Date(dateText + 'T12:00:00');
    if (Number.isNaN(date.getTime())) return null;

    date.setDate(
      date.getDate() + Math.max(0, Number(days) || 0)
    );

    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-');
  }

  return [
    {
      key: 'mid',
      label: 'Mid-Term Self-Evaluation',
      opens: course.mid_eval_open_date,
      closes: closeWithExtension(
        course.mid_eval_close_date,
        course.mid_eval_extension_days
      )
    },
    {
      key: 'final',
      label: 'Final Self-Evaluation',
      opens: course.final_eval_open_date,
      closes: closeWithExtension(
        course.final_eval_close_date,
        course.final_eval_extension_days
      )
    }
  ]
    .map(item => ({
      ...item,
      opens: String(item.opens || '').slice(0, 10)
    }))
    .filter(item =>
      item.opens && item.closes && today <= item.closes
    )
    .map(item => ({
      ...item,
      status: today >= item.opens ? 'open' : 'upcoming'
    }))
    .sort((a, b) =>
      a.status === b.status
        ? a.opens.localeCompare(b.opens)
        : a.status === 'open' ? -1 : 1
    );
}

function MobileCourseDashboard({
  session,
  enrolment,
  weeks,
  attendanceSchedules,
  assignments,
  assignmentDefinitions,
  assignmentTypeSettings,
  assignmentSequences,
  today,
  onBack,
  onOpenTool
}) {
  const course = enrolment?.courses;

  const nextWeek =
    nextWeekForCourse(
      weeks,
      enrolment,
      attendanceSchedules
    );

  const nextPdWeek =
    course?.pd_enabled !== false
      ? nextEnabledWeekForCourse(
          weeks,
          course?.id,
          'pd_enabled'
        )
      : null;

  const nextCqWeek =
    course?.cq_enabled !== false
      ? nextEnabledWeekForCourse(
          weeks,
          course?.id,
          'cq_enabled'
        )
      : null;

  const nextPdDue =
    getMobilePdDueDate(
      course,
      nextPdWeek
    );

  const nextCqDeadline =
    getMobileCqDeadline(
      course,
      nextCqWeek
    );

  const nextAssignments =
    nextAssignmentsForCourse(
      assignments,
      course?.id,
      today
    );

  const courseAssignmentDefinitions =
    (assignmentDefinitions || []).filter(
      assignment =>
        String(assignment.course_id) ===
        String(course?.id)
    );

  return (
    <>
      <ScreenHeader
        title="Course"
        onBack={onBack}
      />

      <div className="vm-scroll with-topbar">
        <section className="vm-course-hero">
          <CourseArtwork course={course} />
        </section>

        <div className="vm-course-title-block">
  <h2>{getCourseName(enrolment)}</h2>

  <CoursePlacement
    session={session}
    enrolment={enrolment}
    variant="mobile"
  />

  <p>
            {[
              course?.semesters?.name,
              courseSchedule(course)
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>

        <div className="vm-upcoming-grid">
          {nextWeek && (
            <div className="vm-upcoming-card">
              <span>Next class</span>

              <strong>
                {formatShortDate(
                  nextWeek.class_date
                )}
              </strong>

              <small>
                {[
                  effectiveClassTimeForStudent(
                    enrolment,
                    nextWeek,
                    attendanceSchedules
                  ),

                  effectiveClassRoomForStudent(
                    enrolment,
                    nextWeek,
                    attendanceSchedules
                  )
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </small>
            </div>
          )}

          {nextPdWeek && nextPdDue && (
            <div className="vm-upcoming-card">
              <span>Philosophical Diary</span>

              <strong>
                Opens{' '}
                {formatShortDate(
                  nextPdWeek.class_date
                )}
              </strong>

              <small>
                Due {formatShortDate(nextPdDue)}
              </small>
            </div>
          )}

          {nextCqWeek && nextCqDeadline && (
            <div className="vm-upcoming-card">
              <span>Questions Assignment</span>

              <strong>
                {nextCqDeadline.deadline.toLocaleDateString(
                  'en-GB',
                  {
                    day: 'numeric',
                    month: 'short'
                  }
                )}
              </strong>

              <small>
                Due {nextCqDeadline.hoursBefore}{' '}
                {nextCqDeadline.hoursBefore === 1
                  ? 'hour'
                  : 'hours'}{' '}
                before class
              </small>
            </div>
          )}


          {getMobileSelfEvalReminders(course, today).map(item => (
            <div className="vm-upcoming-card" key={`selfeval-${item.key}`}>
              <span>{item.label}</span>
              <strong>
                {item.status === 'open'
                  ? 'Open now'
                  : `Opens ${formatShortDate(item.opens)}`}
              </strong>
              <small>Closes {formatShortDate(item.closes)}</small>
            </div>
          ))}

          {nextAssignments.map(
            nextAssignment => {
              const requiresSubmission =
                nextAssignment.assignment_types
                  ?.requires_submission ?? true;

              const dueDate =
                getStudentAssignmentDate(
                  nextAssignment
                );

              const openDate =
                getMobileAssignmentOpenDate(
                  nextAssignment
                );

              const openDateString =
                openDate
                  ? String(openDate).slice(0, 10)
                  : '';

              const isNotYetOpen =
                requiresSubmission &&
                openDateString &&
                openDateString > today;

              return (
                <div
                  className="vm-upcoming-card"
                  key={
                    nextAssignment
                      .student_assignment_occurrence_key ||
                    nextAssignment.id
                  }
                >
                  <span>
                    {nextAssignment.assignment_types
                      ?.name || 'Assignment'}

                    {nextAssignment
                      .schedule_source_title
                      ? ` — ${nextAssignment.schedule_source_title}`
                      : ''}
                  </span>

                  <strong>
                    {isNotYetOpen
                      ? `Opens ${formatShortDate(
                          openDate
                        )}`
                      : formatShortDate(dueDate)}
                  </strong>

                  <small>
                    {isNotYetOpen
                      ? `Due ${formatShortDate(
                          dueDate
                        )}`
                      : requiresSubmission
                        ? 'Due'
                        : 'Scheduled'}
                  </small>
                </div>
              );
            }
          )}
        </div>

        <section className="vm-section">
          <div className="vm-section-label">
            Course
          </div>

          <div className="vm-tool-list">
            {toolItems(
              course,
              courseAssignmentDefinitions,
              assignmentTypeSettings,
              assignmentSequences
            ).map(item => (
              <button
                key={item.key}
                type="button"
                onClick={() =>
                  onOpenTool(item)
                }
              >
                <span>{item.label}</span>
                <Icon name="arrow" />
              </button>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

function MobileCourseTool({
  selectedTool,
  session,
  enrolment,
  onboarding,
  onBack,
  onOnboardingUpdate,
  onOpenAssignmentType = null,
  onBackToSequence = null
}) {
  const course = enrolment?.courses;

  const nestedBackRef = useRef(null);

  function handleScreenBack() {
    if (nestedBackRef.current) {
      const handler = nestedBackRef.current;
      nestedBackRef.current = null;
      handler();
      return;
    }

    onBack();
  }
const title = selectedTool?.label || 'Course';
const toolKey =
  selectedTool?.toolKey ||
  selectedTool?.key;

if (toolKey === 'chat') {
  return (
    <div className="vm-chat-host">
      <Chat
        session={session}
        initialCourseId={
          course?.id || null
        }
        onBack={onBack}
      />
    </div>
  );
}

const commonProps = {
    session,
    course,
    enrolment,
    onboarding,
    onBack,
    onOnboardingUpdate
  };

  let toolContent = null;

  if (toolKey === 'schedule') {
    toolContent = <StudentSchedule {...commonProps} />;
  } else if (toolKey === 'readings') {
    toolContent = (
      <StudentReadings
        course={course}
      />
    );
  } else if (
    toolKey === 'progress' &&
    course
      ?.course_progress_enabled !==
      false
  ) {
    toolContent =
      <StudentProgress {...commonProps} />;
  } else if (toolKey === 'pd') {
    toolContent = <StudentPd {...commonProps} />;
  } else if (toolKey === 'cq') {
    toolContent = <StudentCq {...commonProps} />;
  } else if (toolKey === 'qi') {
    toolContent = <StudentQi {...commonProps} />;
  } else if (toolKey === 'assignments') {
    toolContent = (
      <StudentAssignments
        {...commonProps}
        selectedAssignmentTypeId={
          selectedTool.assignmentTypeId || null
        }
        selectedSequenceId={
          selectedTool.sequenceId || null
        }
        selectedSequenceName={
          selectedTool.sequenceId
            ? selectedTool.label || 'Assignment Sequence'
            : ''
        }
        onOpenAssignmentType={onOpenAssignmentType}
        onBackToSequence={onBackToSequence}
      />
    );
  } else if (toolKey === 'selfeval') {
    toolContent = <StudentSelfEval {...commonProps} />;
  } else if (toolKey === 'syllabus') {
  toolContent = (
    <StudentSyllabus
      {...commonProps}
      reviewBackRef={nestedBackRef}
    />
  );
} else if (toolKey === 'documents') {
    toolContent = <StudentDocuments {...commonProps} />;
 } else if (toolKey === 'instructor') {
  toolContent = (
    <StudentInstructor
      {...commonProps}
    />
  );
} else if (toolKey === 'voting') {
  toolContent = (
    <VotingDisplay
      session={session}
      course={course}
    />
  );
}

  return (
    <>
      <ScreenHeader
  title={title}
  onBack={handleScreenBack}
/>

      <div className="vm-scroll with-topbar vm-tool-scroll">
        <div className="vm-tool-host">
          {toolContent || (
            <div className="vm-empty">
              This course tool is not available.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function getCheckInState(
  session,
  enrolments,
  weeks,
  attendanceSchedules
) {
  const now = getEffectiveNow(session);
  const today = getEffectiveToday(session);

  const todayWeeks = weeks.filter(
    week =>
      String(week?.class_date || '').slice(0, 10) ===
      today
  );

  if (!todayWeeks.length) {
    return {
      status: 'unavailable',
      heading: 'No class available',
      detail:
        `No scheduled class was found for ${today}.`
    };
  }

  const enrolledClasses = todayWeeks
    .map(week => ({
      week,
      enrolment: courseById(
        enrolments,
        week.course_id
      )
    }))
    .filter(item => item.enrolment);

  if (!enrolledClasses.length) {
    return {
      status: 'unavailable',
      heading: 'No eligible class',
      detail:
        'A class exists on this date, but the student is not actively enrolled in it.'
    };
  }

  const attendanceClasses =
    enrolledClasses.filter(
      item =>
        item.enrolment?.courses
          ?.attendance_enabled !== false &&
        studentHasClassOnWeek(
          item.enrolment,
          item.week,
          attendanceSchedules
        )
    );

  if (!attendanceClasses.length) {
    return {
      status: 'unavailable',
      heading: 'Attendance disabled',
      detail:
        'Attendance is not enabled for this class.'
    };
  }

  const automatedClasses =
    attendanceClasses.filter(
      item =>
        item.enrolment?.courses
          ?.automated_checkin_enabled === true
    );

  if (!automatedClasses.length) {
    return {
      status: 'unavailable',
      heading: 'Automatic check-in disabled',
      detail:
        'Automated Check-In is not enabled for this course.'
    };
  }

  const candidates = automatedClasses
    .flatMap(item => {
      const baseTime =
        courseTimeForDate(
          item.enrolment.courses,
          item.week.class_date
        );

      const effectiveTime =
        effectiveClassTimeForStudent(
          item.enrolment,
          item.week,
          attendanceSchedules
        );

      const times = [
        ...new Set(
          [
            baseTime,
            effectiveTime
          ].filter(Boolean)
        )
      ];

      return times.map(
        time => {
          const start =
            new Date(
              `${today}T${time}:00`
            );

          const open =
            new Date(
              start.getTime() -
              20 * 60 * 1000
            );

          const close =
            new Date(
              start.getTime() +
              10 * 60 * 1000
            );

          return {
            ...item,
            time,
            start,
            open,
            close,
            isOverride:
              !!effectiveTime &&
              effectiveTime !==
                baseTime &&
              time ===
                effectiveTime
          };
        }
      );
    })
    .sort(
      (a, b) =>
        a.start.getTime() -
        b.start.getTime()
    );

  if (!candidates.length) {
    return {
      status: 'unavailable',
      heading: 'Class time missing',
      detail:
        'The class exists, but no class time could be determined.'
    };
  }

  const active =
    candidates.find(
      item =>
        now >= item.open &&
        now <= item.close
    );

  if (active) {
    return {
      status: 'ready',
      heading: 'Ready to check in',
      detail:
        `Effective time: ${now.toLocaleTimeString(
          'en-GB',
          {
            hour: '2-digit',
            minute: '2-digit'
          }
        )}.`,
      current: active
    };
  }


  const next =
    candidates.find(
      item =>
        now < item.open
    );

  if (next) {
    return {
      status: 'unavailable',
      heading:
        'Check-in has not opened',
      detail:
        `Effective time: ${now.toLocaleTimeString(
          'en-GB',
          {
            hour: '2-digit',
            minute: '2-digit'
          }
        )}. Check-in opens at ${next.open.toLocaleTimeString(
          'en-GB',
          {
            hour: '2-digit',
            minute: '2-digit'
          }
        )}.`,
      current: next
    };
  }


  const latest =
    candidates[
      candidates.length - 1
    ];

  return {
    status: 'unavailable',
    heading:
      'Check-in has closed',
    detail:
      `Effective time: ${now.toLocaleTimeString(
        'en-GB',
        {
          hour: '2-digit',
          minute: '2-digit'
        }
      )}. Check-in closed at ${latest.close.toLocaleTimeString(
        'en-GB',
        {
          hour: '2-digit',
          minute: '2-digit'
        }
      )}.`,
    current: latest
  };
}

function MobileCheckIn({
  session,
  enrolments,
  weeks,
  attendanceSchedules
}) {
  const [scanStatus, setScanStatus] = useState('idle');
  const [devMessage, setDevMessage] = useState('');

  const state = getCheckInState(
    session,
    enrolments,
    weeks,
    attendanceSchedules
  );

  const currentCourse =
    state.current?.enrolment;

    const canCheckIn =
  state.status === 'ready' &&
  scanStatus !== 'scanning' &&
  scanStatus !== 'submitting' &&
  scanStatus !== 'checked_in';

  async function handleScan() {
  if (!canCheckIn) {
    return;
  }

  setScanStatus('scanning');
  setDevMessage('Looking for the classroom signal...');

  try {
    const result = await scanForVeritasSignal(6000);

    if (!result?.found || !result?.signalToken) {
      setScanStatus('not_found');
      setDevMessage(
        'No classroom signal was found. You have NOT been checked in.'
      );
      return;
    }

    setScanStatus('submitting');
    setDevMessage(
      'Classroom signal found. Recording attendance...'
    );

    let attendanceResult;

    if (session?.isMaster) {
      const courseId =
        state.current?.week?.course_id ||
        currentCourse?.course_id ||
        currentCourse?.courses?.id;

      if (!courseId) {
        throw new Error(
          'The current course could not be identified.'
        );
      }

      attendanceResult = await submitStudentAttendance({
        studentId: session.studentId,
        courseId,
        testMode: true,
        effectiveAt: getEffectiveNow(session).toISOString()
      });
    } else {
      const deviceToken = await getNativeDeviceToken();

      if (!deviceToken) {
        throw new Error(
          'This phone does not have a valid saved app login. ' +
          'Sign out and sign in again before checking in.'
        );
      }

      attendanceResult = await submitStudentAttendance({
        studentId: session.studentId,
        signalToken: result.signalToken,
        deviceToken
      });
    }

    if (
      attendanceResult?.status === 'checked_in' ||
      attendanceResult?.status === 'already_checked_in'
    ) {
      setScanStatus('checked_in');

      setDevMessage(
        attendanceResult.status === 'already_checked_in'
          ? 'You are already checked in for this class.'
          : 'Attendance recorded successfully.'
      );

      return;
    }

    setScanStatus('error');

    setDevMessage(
      attendanceResult?.message ||
        'Attendance could not be recorded. You have NOT been checked in.'
    );
  } catch (error) {
    console.error('Attendance check-in failed:', error);

    setScanStatus('error');

    setDevMessage(
      error?.message ||
        'Attendance could not be recorded. You have NOT been checked in.'
    );
  }
}

  return (
    <>
      <ScreenHeader title="Check In" />

      <div className="vm-scroll with-topbar">
        {currentCourse ? (
          <div className="vm-check-course">
            <span>Current class</span>
            <strong>
              {getCourseName(currentCourse)}
            </strong>

            <small>
              {[
                dayName(
                  state.current
                    .week
                    .class_date
                ),

                state.current.time,

                effectiveClassRoomForStudent(
                  currentCourse,
                  state.current.week,
                  attendanceSchedules
                )
              ]
                .filter(Boolean)
                .join(' · ')}
            </small>

            <div
              className={
                `vm-check-state ${state.status}`
              }
            >
              {state.heading}
            </div>
          </div>
        ) : (
          <div className="vm-check-course unavailable-card">
            <span>Current class</span>
            <strong>No eligible class</strong>
            <small>{state.detail}</small>
          </div>
        )}

        <button
  className="vm-check-action"
  type="button"
  disabled={!canCheckIn}
  onClick={handleScan}
>
          <strong>
  {scanStatus === 'checked_in'
    ? 'CHECKED IN'
    : scanStatus === 'submitting'
      ? 'Recording attendance...'
      : scanStatus === 'scanning'
        ? 'Looking for classroom...'
        : 'CHECK IN'}
</strong>

<small>
  {scanStatus === 'scanning'
    ? 'Keep this screen open'
    : 'Tap here to check in'}
</small>
        </button>

        <p className="vm-check-help">
          The app handles classroom signal and timing.
        </p>

        <div
  className={
    `vm-status-card ${
      scanStatus === 'not_found' || scanStatus === 'error'
        ? 'failure'
        : ''
    }`
  }
>
          <span>Status</span>

          <strong>
  {scanStatus === 'checked_in'
    ? 'CHECKED IN'
    : scanStatus === 'submitting'
      ? 'Recording attendance...'
      : scanStatus === 'scanning'
        ? 'Looking for classroom...'
        : scanStatus === 'not_found'
          ? 'NOT CHECKED IN'
          : scanStatus === 'error'
            ? 'NOT CHECKED IN'
            : state.status === 'ready'
              ? 'Not checked in yet'
              : state.heading}
</strong>

          {state.status !== 'ready' && (
            <small>{state.detail}</small>
          )}

          {devMessage && (
            <small>{devMessage}</small>
          )}
        </div>
      </div>
    </>
  );
}

function MobileChat({ chats, onOpenChat }) {
  return (
    <>
      <ScreenHeader title="Chat" />

      <div className="vm-scroll with-topbar">
        <section className="vm-section">
          <div className="vm-section-label">Recent</div>

          {chats.length ? (
            <div className="vm-chat-list">
              {chats.map(chat => (
                <button
                  key={chat.id}
                  type="button"
                  onClick={() => onOpenChat(chat)}
                >
                  <span className="vm-chat-avatar">
                    {String(
                      chat.label ||
                      chat.name ||
                      chat.title ||
                      'C'
                    )
                      .charAt(0)
                      .toUpperCase()}
                  </span>

                  <span className="vm-chat-copy">
                    <strong>
                      {chat.label ||
                        chat.name ||
                        chat.title ||
                        'Course Chat'}
                    </strong>

                    <small>
                      {chat.type === 'course'
                        ? 'Course chat'
                        : chat.type === 'group'
                          ? 'Group chat'
                          : chat.type === 'honors'
                            ? 'Honors'
                            : 'Conversation'}
                    </small>
                  </span>

                  <Icon name="arrow" />
                </button>
              ))}
            </div>
          ) : (
            <div className="vm-empty">No chats available.</div>
          )}
        </section>
      </div>
    </>
  );
}

function MobileChatThread({ session, chat, onBack }) {
  return (
    <div className="vm-chat-host">
      <Chat
        session={session}
        initialCourseId={chat?.courseId || null}
        initialChatId={chat?.id || null}
        onBack={onBack}
      />
    </div>
  );
}

function MobileDevices({
  session,
  onBack,
  onLogout
}) {
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [deviceError, setDeviceError] = useState('');

  async function loadDevices() {
    setLoadingDevices(true);
    setDeviceError('');

    try {
      const token = await getNativeDeviceToken();

      if (!token) {
        throw new Error(
          'This device does not have a saved app login.'
        );
      }

      const rows = await listStudentDevices({
        studentId: session.studentId,
        token
      });

      setDevices(rows);
    } catch (err) {
      setDeviceError(
        err?.message || 'Could not load devices.'
      );
    } finally {
      setLoadingDevices(false);
    }
  }

  useEffect(() => {
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.studentId]);

  async function handleRevoke(device) {
    const name =
      device.device_name ||
      `${devicePlatformLabel(device.platform)} device`;

    if (
      !window.confirm(
        `Sign out ${name}? It will need to log in again before using the student app.`
      )
    ) {
      return;
    }

    setBusyId(device.id);
    setDeviceError('');

    try {
      const token = await getNativeDeviceToken();

      if (!token) {
        throw new Error(
          'This device does not have a saved app login.'
        );
      }

      const result = await revokeStudentDevice({
        studentId: session.studentId,
        deviceSessionId: device.id,
        token
      });

      if (result.current_device_revoked) {
        await onLogout();
        return;
      }

      setDevices(current =>
        current.filter(item => item.id !== device.id)
      );
    } catch (err) {
      setDeviceError(
        err?.message || 'Could not sign out that device.'
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <ScreenHeader title="Devices" onBack={onBack} />

      <div className="vm-scroll with-topbar">
        <section className="vm-section">
          <div className="vm-section-label">Signed in</div>

          <p className="vm-devices-intro">
            These devices have a saved student app login.
            Signing one out means it will need to log in again.
          </p>

          {deviceError && (
            <div className="vm-error vm-device-error">
              {deviceError}
            </div>
          )}

          {loadingDevices ? (
            <div className="vm-empty">
              Loading devices...
            </div>
          ) : devices.length ? (
            <div className="vm-device-list">
              {devices.map(device => (
                <article
                  className="vm-device-row"
                  key={device.id}
                >
                  <div
                    className="vm-device-glyph"
                    aria-hidden="true"
                  >
                    <svg
                      width="21"
                      height="21"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="6.5"
                        y="2.5"
                        width="11"
                        height="19"
                        rx="1.8"
                      />
                      <path d="M10 18.5h4" />
                    </svg>
                  </div>

                  <div className="vm-device-copy">
                    <div className="vm-device-name-line">
                      <strong>
                        {device.device_name ||
                          `${devicePlatformLabel(
                            device.platform
                          )} device`}
                      </strong>

                      {device.is_current && (
                        <span className="vm-current-device">
                          This device
                        </span>
                      )}
                    </div>

                    <span>
                      {devicePlatformLabel(device.platform)}
                    </span>

                    <small>
                      Signed in{' '}
                      {formatDeviceMoment(device.created_at)}
                      <br />
                      Last used{' '}
                      {formatDeviceMoment(device.last_used_at)}
                    </small>
                  </div>

                  <button
                    type="button"
                    className="vm-device-signout"
                    disabled={busyId === device.id}
                    onClick={() => handleRevoke(device)}
                  >
                    {busyId === device.id
                      ? 'Signing out...'
                      : device.is_current
                        ? 'Sign Out This Device'
                        : 'Sign Out'}
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="vm-empty">
              No mobile devices are currently signed in.
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function MobileReadings({ session, onBack }) {
  return (
    <>
      <ScreenHeader
        title="Course Readings"
        onBack={onBack}
      />

      <div className="vm-scroll with-topbar vm-tool-scroll">
        <div className="vm-tool-host">
          <StudentAllCourseReadings
            session={session}
            onBack={onBack}
          />
        </div>
      </div>
    </>
  );
}


function MasterTestingControls({ session }) {
  const initialState = getSystemTimeState(session);

  const [mode, setMode] = useState(initialState.mode);
  const [simulatedTime, setSimulatedTime] = useState(
    initialState.simulatedTime ||
      toDateTimeLocalInput(getEffectiveNow(session))
  );

  const [
    honorsStatus,
    setHonorsStatus
  ] =
    useState(
      initialState.honorsStatus ||
      ''
    );

  const [error, setError] = useState('');

 function handleApply() {
  setError('');

  try {
    setSystemTime(
      mode,

      mode === 'simulated'
        ? simulatedTime
        : null,

      session,

      honorsStatus ||
      null
    );
    window.location.reload();
  } catch (err) {
    setError(err?.message || 'Could not change Master Student testing mode.');
  }
}

  return (
    <section style={masterTestingStyles.panel}>
      <div style={masterTestingStyles.kicker}>
        MASTER STUDENT
      </div>

      <h2 style={masterTestingStyles.heading}>
        Testing Controls
      </h2>

      <p style={masterTestingStyles.intro}>
        Control time-dependent behaviour and the simulated
        Honors identity used by the Master Student.
      </p>

      <label
        htmlFor="master-testing-mode"
        style={masterTestingStyles.label}
      >
        Mode
      </label>

      <select
        id="master-testing-mode"
        value={mode}
        onChange={event => {
          setMode(event.target.value);
          setError('');
        }}
        style={masterTestingStyles.select}
      >
        <option value="real">Real Time</option>
        <option value="simulated">
          Simulated Date &amp; Time
        </option>
        <option value="open">Open Everything</option>
      </select>

      {mode === 'simulated' && (
        <div style={masterTestingStyles.timeBlock}>
          <label
            htmlFor="master-simulated-time"
            style={masterTestingStyles.label}
          >
            Simulated date and time
          </label>

          <input
            id="master-simulated-time"
            type="datetime-local"
            value={simulatedTime}
            onChange={event => {
              setSimulatedTime(event.target.value);
              setError('');
            }}
            style={masterTestingStyles.input}
          />
        </div>
      )}

      <div style={masterTestingStyles.timeBlock}>
        <label
          htmlFor="master-honors-testing-status"
          style={masterTestingStyles.label}
        >
          Honors identity
        </label>

        <select
          id="master-honors-testing-status"
          value={honorsStatus}
          onChange={event => {
            setHonorsStatus(
              event.target.value
            );

            setError('');
          }}
          style={masterTestingStyles.select}
        >
          <option value="">
            Actual status
          </option>

          <option value="Honors">
            Honors
          </option>

          <option value="Affiliate">
            Affiliate
          </option>

          <option value="Applicant">
            Applicant
          </option>

          <option value="Non-Honors">
            Non-Honors
          </option>
        </select>
      </div>


      <div style={masterTestingStyles.modeNote}>
        {mode === 'real' && (
          <>
            Uses the phone&apos;s actual date and time.
          </>
        )}

        {mode === 'simulated' && (
          <>
            The selected date and time is used for
            availability, schedules, deadlines and Master
            Student attendance testing.
          </>
        )}

        {mode === 'open' && (
          <>
            Opens time-gated student content. Attendance
            timing is still enforced; use Simulated Date
            &amp; Time to test attendance windows.
          </>
        )}
      </div>

      {error && (
        <div style={masterTestingStyles.error}>


      <div style={masterTestingStyles.modeNote}>
        Honors identity override applies only to Master
        Student Honors testing. It does not change the
        underlying student record.
      </div>

          {error}
        </div>
      )}

      <button
        type="button"
        onClick={handleApply}
        style={masterTestingStyles.applyButton}
      >
        Apply Testing Mode
      </button>
    </section>
  );
}

const masterTestingStyles = {
  panel: {
    margin: '0 16px 18px',
    padding: '18px',
    background: '#fff7ed',
    border: '1px solid #fdba74',
    borderRadius: '14px'
  },
  kicker: {
    color: '#b45309',
    fontSize: '10px',
    fontWeight: 900,
    letterSpacing: '0.12em'
  },
  heading: {
    margin: '5px 0 6px',
    color: '#7c2d12',
    fontSize: '19px'
  },
  intro: {
    margin: '0 0 15px',
    color: '#9a3412',
    fontSize: '13px',
    lineHeight: 1.45
  },
  label: {
    display: 'block',
    marginBottom: '5px',
    color: '#7c2d12',
    fontSize: '12px',
    fontWeight: 800
  },
  select: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 11px',
    border: '1px solid #fdba74',
    borderRadius: '9px',
    background: '#fff',
    color: '#1f2933',
    fontSize: '14px'
  },
  timeBlock: {
    marginTop: '13px'
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 11px',
    border: '1px solid #fdba74',
    borderRadius: '9px',
    background: '#fff',
    color: '#1f2933',
    fontSize: '14px'
  },
  modeNote: {
    marginTop: '13px',
    padding: '10px 11px',
    background: 'rgba(255,255,255,0.7)',
    borderRadius: '9px',
    color: '#9a3412',
    fontSize: '12px',
    lineHeight: 1.45
  },
  error: {
    marginTop: '11px',
    color: '#a33',
    fontSize: '12px',
    fontWeight: 700
  },
  applyButton: {
    width: '100%',
    marginTop: '13px',
    padding: '11px 14px',
    border: 0,
    borderRadius: '9px',
    background: '#b45309',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 900,
    cursor: 'pointer'
  }
};


function honorsRegistrationMessage(
  status
) {
  if (status === 'Accepted') {
    return 'Please attend the first class. If you do not attend the first class, your spot may be given to another student. If you cannot attend the first class, you must contact the instructor to let them know.';
  }

  if (status === 'Waitlisted') {
    return 'This course is currently full. You have been placed on the waiting list. We will contact you if a place becomes available.';
  }

  return '';
}


function honorsStatusClass(
  status
) {
  if (status === 'Accepted') {
    return 'accepted';
  }

  if (status === 'Waitlisted') {
    return 'waitlisted';
  }

  if (status === 'Not accepted') {
    return 'not-accepted';
  }

  return 'pending';
}


function MobileHonors({
  session,
  initialView,
  initialState,
  onStateChange,
  onBack
}) {
  const [
    portalState,
    setPortalState
  ] =
    useState(
      initialState ||
      null
    );

  const [
    loading,
    setLoading
  ] =
    useState(
      !initialState
    );

  const [
    error,
    setError
  ] =
    useState('');

  const [
    frameUrl,
    setFrameUrl
  ] =
    useState('');

  const [
    frameType,
    setFrameType
  ] =
    useState('');

  const [
    opening,
    setOpening
  ] =
    useState(false);

  const autoOpened =
    useRef(false);

  const registrationCardRef =
    useRef(null);


  async function loadState() {
    setLoading(true);
    setError('');

    try {
      const state =
        await getStudentHonorsPortalState(
          session
        );

      setPortalState(
        state
      );

      if (onStateChange) {
        onStateChange(
          state
        );
      }

      return state;

    } catch (error) {
      setError(
        error?.message ||
        'The Tunghai Honors Program information could not be loaded.'
      );

      return null;

    } finally {
      setLoading(false);
    }
  }


  useEffect(() => {
    loadState();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  async function openForm(
    formType
  ) {
    setOpening(true);
    setError('');

    try {
      const url =
        await createStudentHonorsFormUrl(
          session,
          formType
        );

      setFrameType(
        formType
      );

      setFrameUrl(
        url
      );

    } catch (error) {
      setError(
        error?.message ||
        'The form could not be opened.'
      );

    } finally {
      setOpening(false);
    }
  }


  useEffect(() => {
    if (
      loading ||
      !portalState ||
      autoOpened.current
    ) {
      return;
    }


    if (
      initialView ===
        'application'
      &&
      portalState
        ?.program
        ?.showApplication
    ) {
      autoOpened.current =
        true;

      openForm(
        'application'
      );

      return;
    }


    if (
      initialView ===
        'course_registration'
      &&
      portalState
        ?.courseRegistration
        ?.open
    ) {
      autoOpened.current =
        true;

      openForm(
        'course_registration'
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    portalState,
    initialView
  ]);


  async function closeForm() {
    setFrameUrl('');
    setFrameType('');

    await loadState();
  }


  const program =
    portalState?.program ||
    {};


  const registration =
    portalState
      ?.courseRegistration ||
    {};


  const selections =
    Array.isArray(
      registration.selections
    )
      ? registration.selections
      : [];


  const recommendations =
    Array.isArray(
      registration.recommendations
    )
      ? registration.recommendations
      : [];


  useEffect(() => {
    if (
      loading ||
      !portalState ||
      initialView !==
        'course_registration' ||
      registration.open
    ) {
      return undefined;
    }


    autoOpened.current =
      true;


    const timer =
      window.setTimeout(
        () => {
          registrationCardRef
            .current
            ?.scrollIntoView({
              behavior:
                'smooth',

              block:
                'start'
            });
        },
        0
      );


    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    loading,
    portalState,
    initialView,
    registration.open
  ]);


  if (frameUrl) {
    return (
      <>
        <ScreenHeader
          title={
            frameType ===
              'application'
              ? 'Application'
              : 'Course Registration'
          }
          onBack={
            closeForm
          }
        />

        <div className="vm-honors-frame-shell">
          <iframe
            className="vm-honors-frame"
            src={frameUrl}
            title={
              frameType ===
                'application'
                ? 'Tunghai Honors Program Application'
                : 'Tunghai Honors Program Course Registration'
            }
          />
        </div>
      </>
    );
  }


  return (
    <>
      <ScreenHeader
        title="Honors"
        onBack={onBack}
        right={
          <button
            type="button"
            className="vm-honors-refresh"
            disabled={
              loading
            }
            onClick={
              loadState
            }
          >
            Refresh
          </button>
        }
      />


      <div className="vm-scroll with-topbar">
        <div className="vm-honors-heading">
          <span>
            Tunghai Honors Program
          </span>

          <h2>
            Honors
          </h2>
        </div>


        {error && (
          <div className="vm-error">
            {error}
          </div>
        )}


        {loading &&
          !portalState ? (
          <div className="vm-empty">
            Loading…
          </div>

        ) : (
          <>
            {program.showApplication ? (
              <section className="vm-honors-card">
                <span className="vm-honors-card-label">
                  Membership
                </span>

                <h3>
                  Apply to the Tunghai Honors Program
                </h3>


                {program
                  .nextInterviewConsiderationDeadline && (
                  <p>
                    Applications received by{' '}
                    <strong>
                      {formatLongDate(
                        new Date(
                          String(
                            program
                              .nextInterviewConsiderationDeadline
                          ).slice(
                            0,
                            10
                          ) +
                          'T12:00:00'
                        )
                      )}
                    </strong>{' '}
                    will be considered for the next interview round.
                    Applications remain open after this date.
                  </p>
                )}


                <button
                  type="button"
                  className="vm-honors-action"
                  disabled={
                    opening
                  }
                  onClick={() =>
                    openForm(
                      'application'
                    )
                  }
                >
                  {opening
                    ? 'Opening…'
                    : 'Open application'}
                </button>
              </section>

            ) : (
              program.status && (
                <section className="vm-honors-card">
                  <span className="vm-honors-card-label">
                    Program status
                  </span>

                  <h3>
                    {program.status}
                  </h3>
                </section>
              )
            )}


            {registration.show && (
              <section
                ref={registrationCardRef}
                className="vm-honors-card"
              >
                <div className="vm-honors-card-top">
                  <div>
                    <span className="vm-honors-card-label">
                      Course Registration
                    </span>

                    <h3>
                      {registration
                        .semesterLabel ||
                        'Honors courses'}
                    </h3>
                  </div>

                  <span
                    className={
                      registration.open
                        ? 'vm-honors-window open'
                        : 'vm-honors-window'
                    }
                  >
                    {registration.open
                      ? 'Open'
                      : 'Closed'}
                  </span>
                </div>


                {registration.open && (
                  <button
                    type="button"
                    className="vm-honors-action"
                    disabled={
                      opening
                    }
                    onClick={() =>
                      openForm(
                        'course_registration'
                      )
                    }
                  >
                    {opening
                      ? 'Opening…'
                      : registration
                          .hasSubmission
                        ? 'Open Course Registration'
                        : 'Pre-register for courses'}
                  </button>
                )}


                {recommendations.length >
                  0 && (
                  <div className="vm-honors-block">
                    <h4>
                      Recommended courses
                    </h4>

                    {recommendations.map(
                      recommendation => (
                        <div
                          className="vm-honors-recommendation"
                          key={
                            recommendation
                              .recommendationId
                          }
                        >
                          <strong>
                            {
                              recommendation
                                .courseName
                            }
                          </strong>

                          <small>
                            Recommended by{' '}
                            {
                              recommendation
                                .recommenderName
                            }
                          </small>
                        </div>
                      )
                    )}
                  </div>
                )}


                {selections.length >
                  0 && (
                  <div className="vm-honors-block">
                    <h4>
                      Current registration status
                    </h4>

                    <p className="vm-honors-live-note">
                      These are the current CMS statuses and may change as registration is processed.
                    </p>


                    {selections.map(
                      selection => {
                        const message =
                          honorsRegistrationMessage(
                            selection
                              .displayStatus
                          );

                        return (
                          <div
                            className="vm-honors-result"
                            key={
                              selection
                                .selectionId
                            }
                          >
                            <div className="vm-honors-result-top">
                              <div>
                                <strong>
                                  {
                                    selection
                                      .courseName
                                  }
                                </strong>

                                {selection
                                  .recommended && (
                                  <small className="vm-honors-recommended">
                                    Recommended
                                  </small>
                                )}
                              </div>

                              <span
                                className={
                                  'vm-honors-status ' +
                                  honorsStatusClass(
                                    selection
                                      .displayStatus
                                  )
                                }
                              >
                                {
                                  selection
                                    .displayStatus
                                }
                              </span>
                            </div>


                            {message && (
                              <p className="vm-honors-status-message">
                                {message}
                              </p>
                            )}


                            {selection
                              .studentNote && (
                              <div className="vm-honors-student-note">
                                {
                                  selection
                                    .studentNote
                                }
                              </div>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
}

function MobileMore({
  session,
  isHonorsMember,
  onLogout,
  onOpenDevices,
  onOpenNotifications,
  onOpenProfile,
  onOpenReadings,
  onOpenIcic,
  icicAvailable
}) {
  return (
    <>
      <ScreenHeader title="More" />

      <div className="vm-scroll with-topbar">
        <div className="vm-profile-card">
          <div className="vm-profile-initial">
            {String(getStudentName(session))
              .charAt(0)
              .toUpperCase()}
          </div>

          <div>
            <strong>{getStudentName(session)}</strong>
            <span>{session?.studentCode}</span>
          </div>
        </div>

        {session?.isMaster && (
          <MasterTestingControls
            session={session}
          />
        )}

        <div className="vm-more-list">
          {icicAvailable && (
            <button
              type="button"
              className="vm-more-link"
              onClick={onOpenIcic}
            >
              <span>
                ICIC Conference
              </span>

              <Icon name="arrow" />
            </button>
          )}

          {isHonorsMember && (
            <button
              type="button"
              className="vm-more-link"
              onClick={onOpenReadings}
            >
              <span>Course Readings</span>
              <Icon name="arrow" />
            </button>
          )}

          <button
            type="button"
            className="vm-more-link"
            onClick={onOpenProfile}
          >
            <span>Profile</span>
            <Icon name="arrow" />
          </button>

          {!session?.isMaster && (
            <button
              type="button"
              className="vm-more-link"
              onClick={onOpenNotifications}
            >
              <span>Notifications</span>
              <Icon name="arrow" />
            </button>
          )}

          <button
            type="button"
            className="vm-more-link"
            onClick={onOpenDevices}
          >
            <span>Devices</span>
            <Icon name="arrow" />
          </button>

          <button
            type="button"
            className="vm-signout-row"
            onClick={onLogout}
          >
            Sign Out
          </button>
        </div>
      </div>
    </>
  );
}

export default function MobileStudentApp({
  session,
  pushTarget,
  onProfileUpdated,
  onLogout
}) {
  const [tab, setTab] = useState('home');
  const [homeView, setHomeView] = useState('home');
  const [currentEnrolments, setCurrentEnrolments] = useState([]);
  const [previousEnrolments, setPreviousEnrolments] = useState([]);
  const [weeks, setWeeks] = useState([]);

  const [
    attendanceSchedules,
    setAttendanceSchedules
  ] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [assignmentDefinitions, setAssignmentDefinitions] =
    useState([]);
  const [assignmentTypeSettings, setAssignmentTypeSettings] =
    useState([]);
  const [assignmentSequences, setAssignmentSequences] =
    useState([]);
  const [notifications, setNotifications] = useState([]);
  const [onboardings, setOnboardings] = useState([]);
  const [chats, setChats] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [showDevices, setShowDevices] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showReadings, setShowReadings] =
    useState(false);

  const [
    showIcic,
    setShowIcic
  ] =
    useState(false);

  const [
    showIcicPoster,
    setShowIcicPoster
  ] =
    useState(false);

  const [
    icicAvailable,
    setIcicAvailable
  ] =
    useState(false);

  const [
    icicPosterState,
    setIcicPosterState
  ] =
    useState(null);

  const [canAccessEvents, setCanAccessEvents] =
    useState(false);

  const [
    honorsEvents,
    setHonorsEvents
  ] = useState([]);

  const [
    selectedEventId,
    setSelectedEventId
  ] = useState(null);

  const [
    honorsPolls,
    setHonorsPolls
  ] = useState([]);

  const [
    selectedPollId,
    setSelectedPollId
  ] = useState(null);

  const [
    hasGroups,
    setHasGroups
  ] =
    useState(false);

  const [
    honorsPortalState,
    setHonorsPortalState
  ] =
    useState(null);

  const [
    showHonors,
    setShowHonors
  ] =
    useState(false);

  const [
    honorsInitialView,
    setHonorsInitialView
  ] =
    useState(null);

  const navigationStackRef =
    useRef([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const hasAutomatedCheckIn =
    !!session.isMaster ||
    currentEnrolments.some(
      enrolment =>
        enrolment?.courses
          ?.attendance_enabled !== false &&
        enrolment?.courses
          ?.automated_checkin_enabled === true
    );

  /*
   * If automated check-in is switched off while the student is
   * already on the Check In screen, return to Home rather than
   * leaving an inaccessible orphan screen open.
   */
  useEffect(() => {
    if (
      !loading &&
      tab === 'checkin' &&
      !hasAutomatedCheckIn
    ) {
      setTab('home');
      setHomeView('home');
    }
  }, [
    loading,
    tab,
    hasAutomatedCheckIn
  ]);

  const today = getEffectiveToday(session);
  const refreshTick = useStudentDataRefreshTick();


  useEffect(() => {
    let cancelled =
      false;

    loadIcicConferenceState(
      session,
      'student'
    )
      .then(
        state => {
          if (!cancelled) {
            setIcicAvailable(
              state?.available ===
                true
            );
          }
        }
      )
      .catch(
        error => {
          console.error(
            'Loading ICIC availability failed:',
            error
          );

          if (!cancelled) {
            setIcicAvailable(
              false
            );
          }
        }
      );

    return () => {
      cancelled =
        true;
    };
  }, [
    session,
    refreshTick
  ]);


  useEffect(() => {
    let cancelled = false;

    loadIcicPosterState(session)
      .then(state => {
        if (!cancelled) setIcicPosterState(state);
      })
      .catch(error => {
        console.error(
          'Loading mobile ICIC poster state failed:',
          error
        );
        if (!cancelled) setIcicPosterState(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    session,
    refreshTick
  ]);


  /*
   * A global refresh is safe only on passive navigation /
   * dashboard screens.
   *
   * Everything where the student may be typing, reading
   * against a timer, completing a form, submitting work,
   * changing settings, etc. remains protected.
   */
  const globalRefreshSafe =
    !selectedTool &&
    !selectedChat &&
    !selectedPollId &&
    !showProfile &&
    !showNotificationSettings &&
    !showDevices &&
    !showReadings &&
    !showIcic &&
    !showIcicPoster &&
    !showHonors &&
    (
      !!selectedCourseId ||
      tab === 'home' ||
      tab === 'courses' ||
      tab === 'chat' ||
      tab === 'more'
    );


  useEffect(() => {
    setStudentDataRefreshSafe(
      globalRefreshSafe
    );
  }, [
    globalRefreshSafe
  ]);


  useEffect(() => {
    return () => {
      resetStudentDataRefreshGate();
    };
  }, []);


  useEffect(() => {
    if (session?.isMaster) return;

    ensureStudentPushRegistration(
      session,
      { askPermission: true }
    ).catch(err => {
      console.error(
        'Student push registration failed:',
        err?.message || String(err)
      );
    });
  }, [session]);


  useEffect(() => {
    if (
      pushTarget?.view ===
      'course_registration'
    ) {
      requestStudentDataRefresh();

      setHonorsInitialView(
        'course_registration'
      );

      setShowHonors(
        true
      );

      return;
    }


    if (
      pushTarget?.view ===
      'icic_poster'
    ) {
      requestStudentDataRefresh();

      navigationStackRef.current =
        [];

      setTab(
        'home'
      );

      setSelectedEventId(
        null
      );

      setSelectedPollId(
        null
      );

      setSelectedCourseId(
        null
      );

      setSelectedTool(
        null
      );

      setSelectedChat(
        null
      );

      setShowDevices(
        false
      );

      setShowNotificationSettings(
        false
      );

      setShowProfile(
        false
      );

      setShowReadings(
        false
      );

      setShowIcic(
        false
      );

      setShowIcicPoster(
        true
      );

      setShowHonors(
        false
      );

      setHonorsInitialView(
        null
      );

      return;
    }


    if (
      pushTarget?.view !==
      'honors_event'
    ) {
      return;
    }


    requestStudentDataRefresh();

    /*
     * A notification can arrive while any
     * nested Student screen is open.
     *
     * Reset the same state that changeTab()
     * resets, then open Events and select
     * the referenced Event.
     */
    navigationStackRef.current =
      [];

    setTab(
      'events'
    );

    setSelectedEventId(
      pushTarget.eventId ||
      null
    );

    setSelectedPollId(
      null
    );

    setSelectedCourseId(
      null
    );

    setSelectedTool(
      null
    );

    setSelectedChat(
      null
    );

    setShowDevices(
      false
    );

    setShowNotificationSettings(
      false
    );

    setShowProfile(
      false
    );

    setShowReadings(
      false
    );

    setShowHonors(
      false
    );

    setHonorsInitialView(
      null
    );
  }, [
    pushTarget
  ]);

  useEffect(() => {
    let cancelled = false;

    async function loadHonorsState() {
      try {
        const state =
          await getStudentHonorsPortalState(
            session
          );

        if (!cancelled) {
          setHonorsPortalState(
            state
          );
        }

      } catch (error) {
        console.error(
          'Checking Tunghai Honors Program state failed:',
          error
        );

        if (!cancelled) {
          setHonorsPortalState(
            null
          );
        }
      }
    }


    loadHonorsState();


    return () => {
      cancelled = true;
    };
    }, [
    session,
    refreshTick
  ]);

  useEffect(() => {
    let cancelled = false;

    listStudentHonorsEvents(
      session
    )
      .then(result => {
        if (cancelled) {
          return;
        }

        const access =
          result?.canAccessEvents ===
            true ||
          session?.isMaster === true;

        setCanAccessEvents(
          access
        );

        setHonorsEvents(
          access &&
          Array.isArray(
            result?.events
          )
            ? result.events
            : []
        );
      })
      .catch(error => {
        console.error(
          'Checking Events access failed:',
          error
        );

        if (!cancelled) {
          setCanAccessEvents(
            session?.isMaster ===
              true
          );

          setHonorsEvents([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    session,
    refreshTick
  ]);

  useEffect(() => {
    let cancelled =
      false;

    listStudentHonorsPolls(
      session
    )
      .then(result => {
        if (!cancelled) {
          setHonorsPolls(
            Array.isArray(
              result?.polls
            )
              ? result.polls
              : []
          );
        }
      })
      .catch(error => {
        console.error(
          'Loading Honors Polls failed:',
          error
        );

        if (!cancelled) {
          setHonorsPolls([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    session,
    refreshTick
  ]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');

      try {
        const [
          current,
          previous
        ] = await Promise.all([
          fetchCurrentEnrolments(
            session.studentId,
            today
          ),
          fetchPreviousEnrolments(
            session.studentId,
            today
          )
        ]);

        if (cancelled) return;

        const ids = current
          .map(enrolment => enrolment?.courses?.id)
          .filter(Boolean);

        const currentEnrolmentIds =
          current
            .map(enrolment => enrolment.id)
            .filter(Boolean);

        const [
  notices,
  loadedWeeks,
  rawAssignments,
  loadedAssignmentTypeSettings,
  loadedAssignmentSequences,
  loadedOnboardings,
  assignmentGroupMemberships,
  assignmentGroupDeadlines,
  assignmentSubmissionTiming,
  loadedAttendanceSchedules
] = await Promise.all([
  fetchVisibleStudentNotifications(
    session,
    ids,
    3
  ),
  fetchUpcomingWeeks(ids, today),
          fetchAssignments(ids),
          fetchAssignmentTypeSettings(ids),
          fetchAssignmentSequences(ids),
          session.isMaster
            ? Promise.resolve([])
            : loadCourseOnboarding(
                session.studentId,
                ids
              ),
          fetchStudentAssignmentGroupMemberships(
            currentEnrolmentIds
          ),
          fetchStudentAssignmentGroupDeadlines(
            ids
          ),
          fetchStudentAssignmentSubmissionTiming(
            currentEnrolmentIds
          ),

          fetchStudentAttendanceSchedules(
            currentEnrolmentIds
          )
        ]);

        if (cancelled) return;

       const baseAssignments =
          applyStudentAssignmentTiming({
            assignments: rawAssignments,
            assignmentTypeSettings:
              loadedAssignmentTypeSettings,
            enrolments: current,
            memberships:
              assignmentGroupMemberships,
            deadlines:
              assignmentGroupDeadlines,
            submissions:
              assignmentSubmissionTiming
          });

        const scheduleContext =
          await loadStudentAssignmentScheduleContext({
            courseIds: ids,
            enrolments: current,
            includeChoiceState:
              !session.isMaster
          });

        if (cancelled) return;

        const loadedAssignments =
          expandStudentAssignmentScheduleOccurrences({
            assignments: baseAssignments,
            assignmentTypeSettings:
              loadedAssignmentTypeSettings,
            enrolments: current,
            memberships:
              assignmentGroupMemberships,
            submissions:
              assignmentSubmissionTiming,
            scheduleContext,
            isMaster: !!session.isMaster
          });

        let loadedHasGroups =
          false;

        try {
          loadedHasGroups =
            await studentHasAnyCurrentGroups(
              current
            );

        } catch (
          groupsError
        ) {
          console.error(
            'Checking mobile Groups availability failed:',
            groupsError
          );
        }

        if (cancelled) {
          return;
        }

        setHasGroups(
          loadedHasGroups
        );

        setCurrentEnrolments(
          current
        );

        setPreviousEnrolments(
          previous
        );
        setNotifications(notices);
        setWeeks(loadedWeeks);

        setAttendanceSchedules(
          loadedAttendanceSchedules || {}
        );
        const displayNameByCourseAndType =
          new Map(
            (Array.isArray(loadedAssignmentTypeSettings)
              ? loadedAssignmentTypeSettings
              : []
            ).map(setting => [
              `${setting.course_id}:${setting.assignment_type_id}`,
              String(setting.display_name || '').trim()
            ])
          );

        const applyDisplayNames = rows =>
          (Array.isArray(rows) ? rows : []).map(assignment => {
            const displayName =
              displayNameByCourseAndType.get(
                `${assignment.course_id}:${assignment.assignment_type_id}`
              );

            if (!displayName) {
              return assignment;
            }

            return {
              ...assignment,
              assignment_types: {
                ...(assignment.assignment_types || {}),
                name: displayName
              }
            };
          });

        setAssignmentDefinitions(
          applyDisplayNames(rawAssignments)
        );
        setAssignments(
          applyDisplayNames(loadedAssignments)
        );
        setAssignmentTypeSettings(
          loadedAssignmentTypeSettings
        );
        setAssignmentSequences(
          loadedAssignmentSequences
        );
        setOnboardings(loadedOnboardings);
      } catch (err) {
        console.error(
          'Mobile student app load failed:',
          err
        );

        if (!cancelled) {
          setError(
            err?.message ||
              'Failed to load the student app.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }

      try {
        const loadedChats =
          await fetchStudentChats(session);

        if (!cancelled) {
          setChats(
            Array.isArray(loadedChats)
              ? loadedChats
              : []
          );
        }
      } catch (err) {
        console.error(
          'Mobile chat list load failed:',
          err
        );
      }
    }

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.studentId, session.isMaster, today, refreshTick]);

  const selectedEnrolment = useMemo(
    () =>
      courseById(
        currentEnrolments,
        selectedCourseId
      ),
    [
      currentEnrolments,
      selectedCourseId
    ]
  );

  const selectedOnboarding = useMemo(
    () =>
      onboardings.find(
        row =>
          String(row.course_id) ===
          String(selectedCourseId)
      ) || null,
    [
      onboardings,
      selectedCourseId
    ]
  );

  function currentView() {
  return {
    tab,
    homeView,
    selectedCourseId,
    selectedTool,
    selectedChat,
    showDevices,
    showNotificationSettings,
    showReadings,
    showIcic,
    showIcicPoster,
    showHonors,
    honorsInitialView
  };
}

function restoreView(view) {
  setTab(view?.tab || 'home');
  setHomeView(view?.homeView || 'home');

  setSelectedCourseId(
    view?.selectedCourseId || null
  );

  setSelectedTool(
    view?.selectedTool || null
  );

  setSelectedChat(
    view?.selectedChat || null
  );

  setShowDevices(
    view?.showDevices === true
  );

  setShowNotificationSettings(
    view?.showNotificationSettings === true
  );

  setShowReadings(
    view?.showReadings === true
  );

  setShowIcic(
    view?.showIcic === true
  );

  setShowIcicPoster(
    view?.showIcicPoster === true
  );

  setShowHonors(
    view?.showHonors === true
  );

  setHonorsInitialView(
    view?.honorsInitialView ||
    null
  );
}

function pushCurrentView() {
  navigationStackRef.current = [
    ...navigationStackRef.current,
    currentView()
  ];
}

function goBack() {
  requestStudentDataRefresh();

  const stack =
    navigationStackRef.current;

  if (!stack.length) {
    setSelectedChat(null);
    setSelectedTool(null);
    setSelectedCourseId(null);
    setShowDevices(false);
    setShowNotificationSettings(false);
    setShowProfile(false);
    setShowReadings(false);
    setShowIcic(false);
    setShowIcicPoster(false);
    setShowHonors(false);
    setHonorsInitialView(null);
    return;
  }

  const previous =
    stack[stack.length - 1];

  navigationStackRef.current =
    stack.slice(0, -1);

  restoreView(previous);
}

function changeTab(nextTab) {
  requestStudentDataRefresh();

  navigationStackRef.current = [];

  if (nextTab === 'home') setHomeView('home');
  setTab(nextTab);
  setSelectedEventId(null);
  setSelectedPollId(null);
  setSelectedCourseId(null);
  setSelectedTool(null);
  setSelectedChat(null);
  setShowDevices(false);
  setShowNotificationSettings(false);
  setShowProfile(false);
  setShowReadings(false);
  setShowIcic(false);
  setShowIcicPoster(false);
  setShowHonors(false);
  setHonorsInitialView(null);
}

async function openCourse(courseId) {
  requestStudentDataRefresh();
  pushCurrentView();

  setSelectedCourseId(courseId);

  const onboarding =
    onboardings.find(
      row =>
        String(row.course_id) ===
        String(courseId)
    );

  const courseHasSyllabus =
    session.isMaster
      ? false
      : await fetchCourseHasSyllabus(
          courseId
        );

  if (
    !session.isMaster &&
    courseHasSyllabus &&
    onboarding &&
    !onboarding.syllabus_completed
  ) {
    setSelectedTool({
      key: 'syllabus',
      label: 'Syllabus',
      forcedOnboarding: true
    });

    return;
  }

  setSelectedTool(null);
}

function openCourseTool(tool) {
  
  pushCurrentView();
  setSelectedTool(tool);
}

function openChat(chat) {
  
  pushCurrentView();
  setSelectedChat(chat);
}

function openDevices() {
  
  pushCurrentView();
  setShowDevices(true);
}

function openNotificationSettings() {
  
  pushCurrentView();
  setShowNotificationSettings(true);
}

function openProfile() {
  
  pushCurrentView();
  setShowProfile(true);
}

function openReadings() {
  
  pushCurrentView();
  setShowReadings(true);
}


function openIcic() {
  pushCurrentView();

  setShowIcic(true);
  setShowIcicPoster(false);
}


function openIcicPoster() {
  pushCurrentView();

  setShowIcic(false);
  setShowIcicPoster(true);
}


function openHonors(
  initialView = null
) {
  requestStudentDataRefresh();
  pushCurrentView();

  setHonorsInitialView(
    initialView
  );

  setShowHonors(true);
}


function openEvent(
  eventId
) {
  requestStudentDataRefresh();

  navigationStackRef.current =
    [];

  setSelectedEventId(
    eventId
  );

  setTab('events');
  setHomeView('home');

  setSelectedCourseId(null);
  setSelectedTool(null);
  setSelectedChat(null);
  setShowDevices(false);
  setShowNotificationSettings(false);
  setShowProfile(false);
  setShowReadings(false);
  setShowHonors(false);
  setHonorsInitialView(null);
}


function openPoll(
  pollId
) {
  setSelectedPollId(
    pollId
  );

  setTab('home');
  setHomeView('home');
  setSelectedEventId(null);
  setSelectedCourseId(null);
  setSelectedTool(null);
  setSelectedChat(null);
  setShowDevices(false);
  setShowNotificationSettings(false);
  setShowProfile(false);
  setShowReadings(false);
  setShowHonors(false);
  setHonorsInitialView(null);
}


function openAssignmentType(typeId) {
  
  pushCurrentView();

  setSelectedTool(current => ({
    ...current,
    assignmentTypeId: typeId
  }));
}

  function handleOnboardingUpdate(field) {
    if (!selectedCourseId) return;

    setOnboardings(current =>
      current.map(row =>
        String(row.course_id) ===
        String(selectedCourseId)
          ? {
              ...row,
              [field]: true
            }
          : row
      )
    );

   if (field === 'syllabus_completed') {
  setSelectedTool(null);
}
}

const isHonorsMember =
  isCurrentHonorsProgramMember(
    honorsPortalState
  );

const selectedHonorsPoll =
  honorsPolls.find(
    poll =>
      String(
        poll.pollId
      ) ===
      String(
        selectedPollId
      )
  ) || null;

let content;

  if (loading) {
    content = (
      <div className="vm-loading">
        Loading...
      </div>
    );
  } else if (error) {
    content = (
      <div className="vm-scroll">
        <div className="vm-error">
          {error}
        </div>
      </div>
    );
} else if (selectedHonorsPoll) {
  content = (
    <>
      <ScreenHeader
        title="Honors Poll"
        onBack={() =>
          setSelectedPollId(
            null
          )
        }
      />

      <div className="vm-scroll with-topbar">
        <HonorsPollForm
          session={session}
          poll={selectedHonorsPoll}
          compact
          onCancel={() =>
            setSelectedPollId(
              null
            )
          }
          onSubmitted={pollId => {
            setHonorsPolls(
              current =>
                current.filter(
                  poll =>
                    String(
                      poll.pollId
                    ) !==
                    String(
                      pollId
                    )
                )
            );

            setSelectedPollId(
              null
            );
          }}
        />
      </div>
    </>
  );
} else if (showHonors) {
  content = (
    <MobileHonors
      session={session}
      initialView={
        honorsInitialView
      }
      initialState={
        honorsPortalState
      }
      onStateChange={
        setHonorsPortalState
      }
      onBack={goBack}
    />
  );
} else if (showIcicPoster) {
  content = (
    <ICICPosterSubmission
      session={session}
      onBack={goBack}
    />
  );

} else if (showIcic) {
  content = (
    <ICICPortal
      session={session}
      actorType="student"
      onBack={goBack}
    />
  );

} else if (showProfile) {
  content = (
    <StudentProfile
      session={session}
      onBack={goBack}
      onSaved={nextSession => {
        if (onProfileUpdated) {
          onProfileUpdated(nextSession);
        }
      }}
    />
  );
} else if (showNotificationSettings) {
  content = (
    <MobileNotificationSettings
      session={session}
      onBack={goBack}
    />
  );
} else if (showDevices) {
  content = (
    <MobileDevices
      session={session}
      onBack={goBack}
      onLogout={onLogout}
    />
  );
} else if (showReadings) {
  content = (
    <MobileReadings
      session={session}
      onBack={goBack}
    />
  );
} else if (selectedChat) {
  content = (
    <MobileChatThread
      session={session}
      chat={selectedChat}
      onBack={goBack}
    />
  );
} else if (
  selectedTool &&
  selectedEnrolment
) {
  content = (
    <MobileCourseTool
      selectedTool={selectedTool}
      session={session}
      enrolment={selectedEnrolment}
      onboarding={selectedOnboarding}
      onBack={goBack}
      onOnboardingUpdate={
        handleOnboardingUpdate
      }
      onOpenAssignmentType={
        selectedTool?.sequenceId
          ? openAssignmentType
          : null
      }
      onBackToSequence={
        selectedTool?.sequenceId &&
        selectedTool?.assignmentTypeId
          ? goBack
          : null
      }
    />
  );
} else if (selectedEnrolment) {
  content = (
    <MobileCourseDashboard
  session={session}
  enrolment={selectedEnrolment}
      weeks={weeks}
      attendanceSchedules={
        attendanceSchedules
      }
      assignments={assignments}
      assignmentDefinitions={
        assignmentDefinitions
      }
      assignmentTypeSettings={
        assignmentTypeSettings
      }
      assignmentSequences={
        assignmentSequences
      }
      today={today}
      onBack={goBack}
      onOpenTool={openCourseTool}
    />
  );
} else if (tab === 'courses') {
  content = (
    <MobileCourses
      current={currentEnrolments}
      previous={previousEnrolments}
      onOpenCourse={openCourse}
    />
  );
} else if (tab === 'checkin') {
  content = (
    <MobileCheckIn
      session={session}
      enrolments={currentEnrolments}
      weeks={weeks}
      attendanceSchedules={
        attendanceSchedules
      }
    />
  );
} else if (
  tab === 'groups' &&
  hasGroups
) {
  content = (
    <MobileGroups
      session={
        session
      }
      enrolments={
        currentEnrolments
      }
    />
  );

} else if (tab === 'chat') {
  content = (
    <MobileChat
      chats={chats}
      onOpenChat={openChat}
    />
  );
} else if (
  tab === 'events' &&
  canAccessEvents
) {
  content = (
    <MobileEvents
      session={session}
      initialEventId={
        selectedEventId
      }
      onAccessChanged={
        setCanAccessEvents
      }
    />
  );
} else if (tab === 'more') {
  content = (
    <MobileMore
      session={session}
      isHonorsMember={
        isHonorsMember
      }
      onOpenDevices={openDevices}
      onOpenNotifications={
        openNotificationSettings
      }
      onOpenProfile={openProfile}
      onOpenReadings={openReadings}
      onOpenIcic={openIcic}
      icicAvailable={
        icicAvailable
      }
      onLogout={onLogout}
    />
  );
} else {
    content = homeView === 'schedule'
      ? <MobileScheduleHome
          session={session}
          enrolments={currentEnrolments}
          honorsPortalState={
            honorsPortalState
          }
          onOpenHome={() =>
            setHomeView(
              'home'
            )
          }
        />
      : <MobileHome
          session={session}
          enrolments={currentEnrolments}
          weeks={weeks}
          attendanceSchedules={
            attendanceSchedules
          }
          assignments={assignments}
          notifications={notifications}
          honorsPortalState={
            honorsPortalState
          }
          honorsEvents={
            honorsEvents
          }
          honorsPolls={
            honorsPolls
          }
          icicPosterState={
            icicPosterState
          }
          onOpenCourse={openCourse}
          onOpenHonors={openHonors}
          onOpenEvent={
            openEvent
          }
          onOpenPoll={
            openPoll
          }
          onOpenIcicPoster={
            openIcicPoster
          }
          onOpenSchedule={() =>
            setHomeView('schedule')
          }
        />;
  }

  return (
  <div
    className={
      Capacitor.getPlatform() === 'ios'
        ? 'vm-shell vm-ios'
        : 'vm-shell'
    }
  >
    <main className="vm-main">
      {content}
    </main>

    {!showHonors &&
      !showIcic &&
      !showIcicPoster &&
      !selectedPollId &&
      !selectedChat &&
      (
        selectedTool?.toolKey ||
        selectedTool?.key
      ) !== 'chat' && (
        <BottomNav
          active={tab}
          onChange={changeTab}
          showEvents={
            canAccessEvents
          }
          showGroups={
            hasGroups
          }
          showCheckIn={
            hasAutomatedCheckIn
          }
        />
      )}
  </div>
);
}
